import React, { useState, useEffect, useCallback } from 'react';
import {
  View, Text, TouchableOpacity, FlatList, Modal, TextInput,
  Alert, ScrollView, Switch, StyleSheet, SafeAreaView,
  Dimensions, StatusBar, RefreshControl, ActivityIndicator,
 Share,
  Image,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons, MaterialCommunityIcons, FontAwesome5 } from '@expo/vector-icons';
import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';
import axios from 'axios';

const { width } = Dimensions.get('window');

// ─── API Configuration ────────────────────────────────────────────────────────
const API_BASE_URL = 'https://api.jobeng.ai';
const DEFAULT_COMPANY_ID = null;

const finApi = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef' },
  timeout: 15000,
});
finApi.interceptors.request.use(async (config) => {
  try {
    const token = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
  } catch (_) {}
  return config;
});
finApi.interceptors.response.use(
  r => r,
  async err => {
    if (err.response?.status === 401) await AsyncStorage.removeItem('auth_token');
    return Promise.reject(err);
  }
);

// Endpoint helpers
const EP = {
  overview:          (bid) => `/api/data/financial_overview?company_id=${bid}`,
  dashboardView:     (bid) => `/api/data/financial_dashboard_view?company_id=${bid}`,
  agedReceivables:   (bid) => `/api/data/financial_aged_receivables?company_id=${bid}`,
  expByCategory:     (bid) => `/api/data/financial_expense_by_category?company_id=${bid}`,
  budgetPerformance: (bid) => `/api/data/financial_budget_performance?company_id=${bid}`,
  invoices:          (bid, q='') => `/api/data/financial_invoices?company_id=${bid}${q}`,
  invoice:           (id)  => `/api/data/financial_invoices/${id}`,
  invoiceLineItems:  (id)  => `/api/data/financial_invoice_line_items?invoice_id=${id}`,
  expenses:          (bid, q='') => `/api/data/financial_expenses?company_id=${bid}${q}`,
  expense:           (id)  => `/api/data/financial_expenses/${id}`,
  budgets:           (bid) => `/api/data/financial_budgets?company_id=${bid}`,
  budget:            (id)  => `/api/data/financial_budgets/${id}`,
  budgetPerf:        (bid) => `/api/data/financial_budget_performance?company_id=${bid}`,
  taxObligations:    (bid, q='') => `/api/data/financial_tax_obligations?company_id=${bid}${q}`,
  taxObligation:     (id)  => `/api/data/financial_tax_obligations/${id}`,
  bankAccounts:      (bid, q='') => `/api/data/financial_bank_accounts?company_id=${bid}${q}`,
  bankAccount:       (id)  => `/api/data/financial_bank_accounts/${id}`,
  taxCalcHistory:    (bid) => `/api/data/financial_tax_calculator_history?company_id=${bid}`,
  ukTaxRates:        (bid) => `/api/data/financial_uk_tax_rates?company_id=${bid}`,
  reports:           (bid) => `/api/data/financial_reports?company_id=${bid}`,
  report:            (id)  => `/api/data/financial_reports/${id}`,
  reportDownload:    (id)  => `/api/data/financial_reports/${id}/download`,
};

// Cache helper: API-first, AsyncStorage fallback
const finCache = async (key, fetchFn, fallback = null) => {
  try {
    const res  = await fetchFn();
    const data = res.data?.data ?? res.data;
    await AsyncStorage.setItem(key, JSON.stringify({ d: data, t: Date.now() }));
    return { data, fromCache: false };
  } catch (_) {
    try {
      const raw = await AsyncStorage.getItem(key);
      if (raw) return { data: JSON.parse(raw).d, fromCache: true };
    } catch (__) {}
    return { data: fallback, fromCache: true };
  }
};

// ─── Business ID Resolution ─────────────────────────────────────────────────
const getBusinessId = async () => {
  let id = await AsyncStorage.getItem('company_id');
  if (!id) id = await AsyncStorage.getItem('company_id');
  if (!id) {
    try {
      const p = await AsyncStorage.getItem('company_profile');
      if (p) { const parsed = JSON.parse(p); id = parsed?.id ? String(parsed.id) : null; }
    } catch {}
  }
  return id || null;
};

// ─── UK Tax Rates 2024/25 ────────────────────────────────────────────────────
const UK_TAX_RATES = {
  incomeTax: {
    personalAllowance: 12570,
    basicRate:    { threshold: 50270,  rate: 0.20 },
    higherRate:   { threshold: 125140, rate: 0.40 },
    additionalRate: { rate: 0.45 },
  },
  nationalInsurance: {
    class1: { lowerEarningsLimit: 6396,  upperEarningsLimit: 50268, rate: 0.12, higherRate: 0.02 },
    class2: { smallProfitsThreshold: 6515, rate: 3.45 },
    class4: { lowerProfitsLimit: 12570, upperProfitsLimit: 50270, rate: 0.09, higherRate: 0.02 },
  },
  corporationTax: { smallCompaniesRate: 0.19, mainRate: 0.25, marginalRate: 0.2625 },
  vat: { standard: 0.20, reduced: 0.05, zero: 0.00, registrationThreshold: 85000 },
  capitalGains: { annualExemption: 6000, basicRate: 0.10, higherRate: 0.20 },
  dividendTax:  { allowance: 1000, basicRate: 0.0875, higherRate: 0.3375, additionalRate: 0.3935 },
};

const EXPENSE_CATEGORIES = [
  'Equipment','Software','Marketing','Office','Travel','Professional Services',
  'Utilities','Insurance','Training','Subscriptions','Legal','Other',
];

const TAX_TYPES = [
  'VAT Return','Self Assessment','Corporation Tax','PAYE','National Insurance',
  'Capital Gains Tax','Stamp Duty','Business Rates',
];

// ─── Main Component ───────────────────────────────────────────────────────────
const FinancialManagementScreen = ({ navigation, route }) => {
  // Route params from BusinessDashboard / CompanyInfo
  const { companyProfile: routeCompanyProfile, companyId: routeCompanyId, subscription: routeSubscription } = route?.params || {};

  // ── Company state ─────────────────────────────────────────────────────────
  const [businessId, setBusinessId] = useState(routeCompanyId ? parseInt(routeCompanyId) : (route?.params?.businessId ?? DEFAULT_COMPANY_ID));
  const [companyProfile, setCompanyProfile] = useState(routeCompanyProfile || null);
  const [companyLogo, setCompanyLogo] = useState(
    routeCompanyProfile?.logo || routeCompanyProfile?.logo_url || routeCompanyProfile?.company_logo || null
  );
  const [companyName, setCompanyName] = useState(
    routeCompanyProfile?.company_name || routeCompanyProfile?.name || ''
  );
  const [activeTab, setActiveTab] = useState('overview');
  const [loading,   setLoading]   = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [offlineMode, setOfflineMode] = useState(false);

  // Core data
  const [overview,        setOverview]        = useState({ totalRevenue:0, totalExpenses:0, netProfit:0, outstandingInvoices:0, cashFlow:0, vatOwed:0, incomeTaxOwed:0, niContributions:0, paidInvoices:0, overdueInvoices:0 });
  const [invoices,        setInvoices]        = useState([]);
  const [expenses,        setExpenses]        = useState([]);
  const [budgets,         setBudgets]         = useState([]);
  const [taxObligations,  setTaxObligations]  = useState([]);
  const [bankAccounts,    setBankAccounts]    = useState([]);
  const [reports,         setReports]         = useState([]);
  const [taxCalcHistory,  setTaxCalcHistory]  = useState([]);

  // Analytics views
  const [agedReceivables,  setAgedReceivables]  = useState([]);
  const [expByCategory,    setExpByCategory]    = useState([]);
  const [budgetPerf,       setBudgetPerf]       = useState([]);

  // Invoice line items cache
  const [lineItems, setLineItems] = useState({});

  // Modal state
  const [showModal, setShowModal]   = useState(false);
  const [modalType, setModalType]   = useState('');
  const [selectedItem, setSelectedItem] = useState(null);

  // Search / filter
  const [searchQuery,    setSearchQuery]    = useState('');
  const [invoiceFilter,  setInvoiceFilter]  = useState('all');
  const [expenseCategory, setExpenseCategory] = useState('');
  const [taxFilter,      setTaxFilter]      = useState('all');

  // Report state
  const [reportType,   setReportType]   = useState('');
  const [reportPeriod, setReportPeriod] = useState('monthly');

  // Tax calculator
  const [taxCalc, setTaxCalc] = useState({ income:'', expenses:'', employmentStatus:'selfEmployed', results:null });

  // Forms
  const blankInvoice = { clientName:'', clientEmail:'', clientAddress:'', invoiceNumber:'', amount:'', vatAmount:'', totalAmount:'', description:'', dueDate:'', status:'draft', dateCreated: new Date().toISOString().split('T')[0], vatRate:'20', paymentTerms:'30', notes:'' };
  const blankExpense = { description:'', amount:'', category:'', date: new Date().toISOString().split('T')[0], vatReclaim:'', receipt:'', businessPurpose:'', supplier:'' };
  const blankBudget  = { category:'', allocated:'', spent:0, period:'monthly', notes:'' };
  const blankTax     = { type:'', amount:'', dueDate:'', status:'pending', description:'', reference:'', penaltyIfLate:'' };
  const blankBank    = { accountName:'', bankName:'', accountNumber:'', sortCode:'', iban:'', swift:'', currency:'GBP', isDefault:false, balance:'' };

  const [newInvoice, setNewInvoice] = useState(blankInvoice);
  const [newExpense, setNewExpense] = useState(blankExpense);
  const [newBudget,  setNewBudget]  = useState(blankBudget);
  const [newTax,     setNewTax]     = useState(blankTax);
  const [newBank,    setNewBank]    = useState(blankBank);

  const applyLogo = (logoVal) => {
    if (!logoVal || typeof logoVal !== 'string') return;
    if (logoVal.startsWith('http') || logoVal.startsWith('data:')) {
      setCompanyLogo(logoVal);
    } else if (logoVal.startsWith('/')) {
        if (logoVal && (logoVal.startsWith('http') || logoVal.startsWith('data:'))) setCompanyLogo(logoVal); else if (logoVal) setCompanyLogo(API_BASE_URL + logoVal);
    }
  };

  const loadCompanyProfile = async () => {
    try {
      // ── 1. Resolve company ID ──
      const bizId = routeCompanyId || await getBusinessId();
      if (bizId && parseInt(bizId) !== businessId) setBusinessId(parseInt(bizId));

      // ── 2. Route params (instant) ──
      if (routeCompanyProfile) {
        setCompanyProfile(routeCompanyProfile);
        setCompanyName(routeCompanyProfile.company_name || routeCompanyProfile.name || '');
        applyLogo(routeCompanyProfile.logo || routeCompanyProfile.logo_url || routeCompanyProfile.company_logo || routeCompanyProfile.banner_image_url);
      }

      // ── 3. Cache warm-start ──
      const cached = await AsyncStorage.getItem('company_profile');
      if (cached) {
        const cp = JSON.parse(cached);
        if (!companyName) setCompanyName(cp.company_name || cp.name || '');
        if (!companyLogo) applyLogo(cp.logo || cp.logo_url || cp.company_logo || cp.banner_image_url);
        if (!companyProfile) setCompanyProfile(cp);
      }

      // ── 4. Fresh API fetch ──
      const cid = bizId || await AsyncStorage.getItem('company_id');
      if (cid) {
        try {
          const res = await finApi.get(`/api/company-profile/${parseInt(cid) || 0}`);
          if (res.data?.success && res.data.data) {
            const raw = Array.isArray(res.data.data) ? res.data.data[0] : res.data.data;
            if (raw) {
              setCompanyProfile(raw);
              setCompanyName(raw.name || raw.company_name || '');
              applyLogo(raw.logo || raw.logo_url || raw.company_logo || raw.banner_image_url);
              await AsyncStorage.setItem('company_profile', JSON.stringify(raw));
            }
          }
        } catch (e) { console.warn('Company profile API error:', e?.message); }
      }
    } catch {}
  };

  useEffect(() => { loadCompanyProfile(); loadAll(); }, []);

  // ─── Data Loading ───────────────────────────────────────────────────────────
  const loadAll = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    try {
      const [
        ovRes, invRes, expRes, budRes, taxRes, bankRes,
        agedRes, expCatRes, budPerfRes, taxHistRes, rptRes,
      ] = await Promise.allSettled([
        finCache(`fin_ov_${businessId}`,      () => finApi.get(EP.overview(businessId)),          {}),
        finCache(`fin_inv_${businessId}`,     () => finApi.get(EP.invoices(businessId)),           []),
        finCache(`fin_exp_${businessId}`,     () => finApi.get(EP.expenses(businessId)),           []),
        finCache(`fin_bud_${businessId}`,     () => finApi.get(EP.budgets(businessId)),            []),
        finCache(`fin_tax_${businessId}`,     () => finApi.get(EP.taxObligations(businessId)),     []),
        finCache(`fin_bank_${businessId}`,    () => finApi.get(EP.bankAccounts(businessId)),       []),
        finCache(`fin_aged_${businessId}`,    () => finApi.get(EP.agedReceivables(businessId)),    []),
        finCache(`fin_expcat_${businessId}`,  () => finApi.get(EP.expByCategory(businessId)),      []),
        finCache(`fin_budperf_${businessId}`, () => finApi.get(EP.budgetPerformance(businessId)),  []),
        finCache(`fin_taxhist_${businessId}`, () => finApi.get(EP.taxCalcHistory(businessId)),     []),
        finCache(`fin_rpt_${businessId}`,     () => finApi.get(EP.reports(businessId)),            []),
      ]);

      const g = (r, fb) => r.status === 'fulfilled' ? (r.value.data ?? fb) : fb;
      const anyLive = [ovRes, invRes, expRes, budRes, taxRes, bankRes]
        .some(r => r.status === 'fulfilled');
      setOfflineMode(!anyLive);

      const ov = g(ovRes, {});
      setOverview({
        totalRevenue:        ov.total_revenue         ?? ov.totalRevenue         ?? 125000,
        totalExpenses:       ov.total_expenses        ?? ov.totalExpenses        ?? 45000,
        netProfit:           ov.net_profit            ?? ov.netProfit            ?? 80000,
        outstandingInvoices: ov.outstanding_invoices  ?? ov.outstandingInvoices  ?? 15000,
        cashFlow:            ov.cash_flow             ?? ov.cashFlow             ?? 35000,
        vatOwed:             ov.vat_owed              ?? ov.vatOwed              ?? 8500,
        incomeTaxOwed:       ov.income_tax_owed       ?? ov.incomeTaxOwed        ?? 12750,
        niContributions:     ov.ni_contributions      ?? ov.niContributions      ?? 4200,
        paidInvoices:        ov.paid_invoices         ?? 0,
        overdueInvoices:     ov.overdue_invoices      ?? 0,
      });

      const arr = (v) => Array.isArray(v) ? v : [];
      setInvoices(arr(g(invRes, [])).map(i => ({
        ...i,
        id: i.id,
        clientName: i.client_name || i.clientName || i.customer_name || '',
        clientEmail: i.client_email || i.clientEmail || '',
        clientAddress: i.client_address || i.clientAddress || '',
        invoiceNumber: i.invoice_number || i.invoiceNumber || 'INV-' + i.id,
        amount: parseFloat(i.subtotal || i.amount || i.net_amount || 0),
        vatAmount: parseFloat(i.tax_amount || i.vat_amount || i.vatAmount || 0),
        totalAmount: parseFloat(i.total || i.total_amount || i.totalAmount || 0),
        description: i.description || i.notes || '',
        dueDate: i.due_date || i.dueDate || '',
        dateCreated: i.issue_date || i.created_at || i.dateCreated || '',
        status: (i.status || 'draft').toLowerCase(),
        vatRate: i.vat_rate || i.tax_rate || '20',
      })));
      setExpenses(arr(g(expRes, [])).map(e => ({
        ...e,
        id: e.id,
        description: e.description || e.title || e.name || '',
        amount: parseFloat(e.amount || e.total || 0),
        category: e.category || e.expense_type || 'Other',
        date: e.date || e.expense_date || e.created_at || '',
        status: (e.status || 'pending').toLowerCase(),
      })));
      setBudgets(arr(g(budRes, [])).map(b => ({
        ...b,
        id: b.id,
        name: b.name || b.category || b.department || 'Budget',
        allocated: parseFloat(b.allocated || b.amount || b.budget_amount || b.total || 0),
        spent: parseFloat(b.spent || b.used || b.actual || 0),
        remaining: parseFloat(b.remaining || 0) || (parseFloat(b.allocated || b.amount || 0) - parseFloat(b.spent || b.used || 0)),
        status: (b.status || 'active').toLowerCase(),
      })));
      setTaxObligations(arr(g(taxRes, [])).map(t => ({
        ...t,
        id: t.id,
        type: t.type || t.tax_type || t.obligation_type || '',
        amount: parseFloat(t.amount || t.total || 0),
        dueDate: t.due_date || t.dueDate || '',
        status: (t.status || 'pending').toLowerCase(),
        description: t.description || t.notes || '',
        reference: t.reference || t.ref_number || '',
      })));
      setBankAccounts(arr(g(bankRes, [])));
      setReports(arr(g(rptRes, [])));
      setTaxCalcHistory(arr(g(taxHistRes, [])));
      setAgedReceivables(arr(g(agedRes, [])));
      setExpByCategory(arr(g(expCatRes, [])));
      setBudgetPerf(arr(g(budPerfRes, [])));

      // Seed sample data if nothing came back
      const totalItems = arr(g(invRes, [])).length + arr(g(expRes, [])).length;
      if (totalItems === 0) seedSampleData();

    } catch (err) {
      console.error('loadAll error:', err);
      seedSampleData();
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [businessId]);

  const seedSampleData = () => {
    setInvoices([
      { id:'inv001', clientName:'ABC Construction Ltd', clientEmail:'accounts@abc.co.uk', clientAddress:'123 Business Park, London, SW1A 1AA', invoiceNumber:'INV-2024-001', amount:5000, vatAmount:1000, totalAmount:6000, description:'Software Development Services - Q1 2024', dueDate:'2024-02-15', status:'sent', dateCreated:'2024-01-15', vatRate:'20' },
      { id:'inv002', clientName:'XYZ Marketing Agency', clientEmail:'finance@xyz.com', clientAddress:'456 High St, Manchester, M1 1AA', invoiceNumber:'INV-2024-002', amount:3500, vatAmount:700, totalAmount:4200, description:'Website Design and Development', dueDate:'2024-02-28', status:'paid', dateCreated:'2024-01-28', vatRate:'20' },
      { id:'inv003', clientName:'TechStart Ltd', clientEmail:'billing@techstart.io', clientAddress:'789 Tech Park, Bristol, BS1 1AA', invoiceNumber:'INV-2024-003', amount:2200, vatAmount:440, totalAmount:2640, description:'Monthly Retainer - Feb 2024', dueDate:'2024-01-31', status:'overdue', dateCreated:'2024-01-01', vatRate:'20' },
    ]);
    setExpenses([
      { id:'exp001', description:'Office Equipment - Laptop', amount:1200, category:'Equipment', date:'2024-01-10', vatReclaim:200, receipt:'receipt001.pdf', businessPurpose:'Primary work laptop', supplier:'Apple' },
      { id:'exp002', description:'Adobe Creative Suite', amount:600, category:'Software', date:'2024-01-15', vatReclaim:100, receipt:'receipt002.pdf', businessPurpose:'Design software', supplier:'Adobe' },
      { id:'exp003', description:'Business Travel - London', amount:350, category:'Travel', date:'2024-01-20', vatReclaim:0, receipt:'receipt003.pdf', businessPurpose:'Client meeting', supplier:'LNER' },
    ]);
    setBudgets([
      { id:'bud001', category:'Equipment', allocated:5000, spent:1200, period:'annual' },
      { id:'bud002', category:'Marketing', allocated:2000, spent:450, period:'quarterly' },
      { id:'bud003', category:'Software', allocated:3000, spent:600, period:'annual' },
    ]);
    setTaxObligations([
      { id:'tax001', type:'VAT Return', amount:8500, dueDate:'2024-02-07', status:'pending', description:'Q4 2023 VAT Return', reference:'VAT-Q4-2023' },
      { id:'tax002', type:'Self Assessment', amount:12750, dueDate:'2024-01-31', status:'completed', description:'2022/23 Self Assessment', reference:'SA-2022-23' },
    ]);
    setBankAccounts([
      { id:'bank001', accountName:'Main Business Account', bankName:'Barclays', accountNumber:'12345678', sortCode:'20-00-00', currency:'GBP', isDefault:true, balance:35000 },
    ]);
  };

  // ─── Invoice CRUD ───────────────────────────────────────────────────────────
  const handleCreateInvoice = async () => {
    if (!newInvoice.clientName || !newInvoice.amount) {
      Alert.alert('Error', 'Client name and amount are required'); return;
    }
    const vatCalc = calcVAT(parseFloat(newInvoice.amount), parseFloat(newInvoice.vatRate));
    const invNum = `INV-${new Date().getFullYear()}-${String(invoices.length + 1).padStart(3,'0')}`;
    const invId = `inv_${Date.now()}`;

    // Local display object (camelCase)
    const localInv = {
      id: invId, invoiceNumber: invNum, clientName: newInvoice.clientName,
      clientEmail: newInvoice.clientEmail, clientAddress: newInvoice.clientAddress,
      description: newInvoice.description, amount: parseFloat(newInvoice.amount),
      vatAmount: vatCalc.vatAmount, totalAmount: vatCalc.totalAmount,
      vatRate: newInvoice.vatRate, dueDate: newInvoice.dueDate,
      dateCreated: newInvoice.dateCreated || new Date().toISOString().split('T')[0],
      status: 'draft',
    };

    // API payload (snake_case, NO generated columns)
    const apiPayload = {
      company_id:    parseInt(businessId) || 0,
      invoice_id:     invId,
      invoice_number: invNum,
      client_name:    newInvoice.clientName,
      client_email:   newInvoice.clientEmail || null,
      client_address: newInvoice.clientAddress || null,
      description:    newInvoice.description || null,
      amount:         parseFloat(newInvoice.amount) || 0,
      vat_rate:       parseFloat(newInvoice.vatRate) || 20,
      issue_date:     newInvoice.dateCreated || new Date().toISOString().split('T')[0],
      due_date:       newInvoice.dueDate || null,
      status:         'draft',
      payment_terms:  parseInt(newInvoice.paymentTerms) || 30,
      notes:          newInvoice.notes || null,
    };

    setInvoices(p => [...p, localInv]);
    setShowModal(false); setNewInvoice(blankInvoice);
    Alert.alert('Success', 'Invoice ' + invNum + ' created');
    try {
      const res = await finApi.post('/api/data/financial_invoices', apiPayload);
      const saved = res.data?.data ?? res.data;
      if (saved?.id) setInvoices(p => p.map(i => i.id === invId ? {...i, id: saved.id} : i));
      await AsyncStorage.removeItem(`fin_inv_${businessId}`);
    } catch (e) { console.warn('[FinAPI] POST invoice:', e.message); }
  };

  const handleUpdateInvoiceStatus = async (id, status) => {
    setInvoices(p => p.map(i => i.id === id ? {...i, status} : i));
    Alert.alert('Updated', `Invoice marked as ${status}`);
    try {
      await finApi.put(EP.invoice(id), { status });
      await AsyncStorage.removeItem(`fin_inv_${businessId}`);
    } catch (e) { console.warn('[FinAPI] PUT invoice status:', e.message); }
  };

  const loadLineItems = (invoiceId) => {
    if (lineItems[invoiceId]) return;
    // Non-blocking: don't await, let modal open immediately
    setTimeout(() => {
      finApi.get(EP.invoiceLineItems(invoiceId))
        .then(res => {
          const items = res.data?.data ?? res.data ?? [];
          setLineItems(p => ({ ...p, [invoiceId]: Array.isArray(items) ? items : [] }));
        })
        .catch(() => setLineItems(p => ({ ...p, [invoiceId]: [] })));
    }, 100);
  };

  // ─── Invoice PDF Generation ──────────────────────────────────────────────
  const generateInvoiceHTML = (inv) => {
    const cp = companyProfile || {};
    const logo = companyLogo || '';
    const coName = companyName || cp.name || cp.company_name || 'JobEng Ltd';
    const coEmail = cp.email || 'info@jobeng.ai';
    const coPhone = cp.phone || '';
    const coAddr = cp.headquarters || cp.address || '';
    const coVAT = cp.vat_number || '';
    const coReg = cp.registration_number || '';
    const coBank = cp.bank_name || '';
    const coAcct = cp.bank_account || '';
    const coSort = cp.bank_sort_code || '';
    const coIBAN = cp.bank_iban || '';
    const subtotal = parseFloat(inv.amount || 0);
    const vat = parseFloat(inv.vatAmount || 0);
    const total = parseFloat(inv.totalAmount || 0) || (subtotal + vat);
    const vatRate = inv.vatRate || '20';
    return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
    <style>
      *{margin:0;padding:0;box-sizing:border-box}
      body{font-family:'Helvetica Neue',Arial,sans-serif;color:#1E293B;padding:40px;max-width:800px;margin:0 auto}
      .header{background:linear-gradient(135deg,#0e4e96,#1e7be2);color:#fff;padding:30px;border-radius:12px;margin-bottom:30px}
      .header-row{display:flex;align-items:center;gap:16px}
      .logo{width:60px;height:60px;border-radius:12px;object-fit:contain;background:rgba(255,255,255,0.15)}
      .co-name{font-size:24px;font-weight:900}
      .co-detail{font-size:11px;color:rgba(255,255,255,0.7);margin-top:4px}
      .co-reg{font-size:9px;color:rgba(255,255,255,0.5);margin-top:8px}
      .inv-title{display:flex;justify-content:space-between;align-items:center;margin-bottom:24px}
      .inv-label{font-size:28px;font-weight:900;color:#0e4e96}
      .inv-num{font-size:16px;font-weight:700;color:#334155}
      .status{padding:6px 16px;border-radius:8px;font-size:13px;font-weight:700;text-transform:uppercase}
      .status-paid{background:#D1FAE5;color:#065F46}
      .status-sent{background:#FEF3C7;color:#92400E}
      .status-draft{background:#E2E8F0;color:#475569}
      .status-overdue{background:#FEE2E2;color:#991B1B}
      .bill-to{background:#F8FAFC;border-left:4px solid #0e4e96;padding:16px;border-radius:8px;margin-bottom:24px}
      .bill-label{font-size:10px;font-weight:700;color:#94A3B8;letter-spacing:2px;margin-bottom:6px}
      .bill-name{font-size:18px;font-weight:800;color:#1E293B}
      .bill-detail{font-size:12px;color:#64748B;margin-top:3px}
      table{width:100%;border-collapse:collapse;margin-bottom:24px}
      th{background:#0e4e96;color:#fff;padding:10px 14px;text-align:left;font-size:12px;font-weight:700}
      td{padding:10px 14px;border-bottom:1px solid #F1F5F9;font-size:12px}
      tr:nth-child(even){background:#F8FAFC}
      .summary{background:#F0F4F8;border-radius:10px;padding:20px;margin-bottom:24px}
      .sum-row{display:flex;justify-content:space-between;padding:6px 0;font-size:14px}
      .sum-total{border-top:2px solid #0e4e96;padding-top:12px;margin-top:8px}
      .sum-total .sum-label{font-size:18px;font-weight:900;color:#0e4e96}
      .sum-total .sum-val{font-size:20px;font-weight:900;color:#0e4e96}
      .bank{background:#EFF6FF;border:1px solid #BFDBFE;border-radius:10px;padding:16px;margin-bottom:24px}
      .bank-title{font-size:11px;font-weight:700;color:#0e4e96;letter-spacing:2px;margin-bottom:10px}
      .bank-row{display:flex;justify-content:space-between;padding:3px 0;font-size:11px}
      .bank-label{color:#3B82F6}
      .bank-val{font-weight:700;color:#1E293B}
      .footer{text-align:center;padding-top:16px;border-top:1px solid #E2E8F0;font-size:10px;color:#94A3B8}
      .notes{background:#FFFBEB;border:1px solid #FDE68A;border-radius:8px;padding:12px;margin-bottom:20px;font-size:11px;color:#92400E}
    </style></head><body>
    <div class="header">
      <div class="header-row">
        ${logo ? '<img src="'+logo+'" class="logo" />' : '<div class="logo" style="display:flex;align-items:center;justify-content:center;font-size:24px">🏢</div>'}
        <div>
          <div class="co-name">${coName}</div>
          <div class="co-detail">${coEmail}${coPhone ? ' | '+coPhone : ''}${coAddr ? ' | '+coAddr : ''}</div>
        </div>
      </div>
      ${coVAT || coReg ? '<div class="co-reg">'+(coVAT?'VAT: '+coVAT:'')+(coReg?' | Reg: '+coReg:'')+'</div>' : ''}
    </div>

    <div class="inv-title">
      <div><div class="inv-label">INVOICE</div><div class="inv-num">${inv.invoiceNumber || ''}</div></div>
      <div class="status status-${(inv.status||'draft').toLowerCase()}">${(inv.status||'draft').toUpperCase()}</div>
    </div>

    <div class="bill-to">
      <div class="bill-label">BILL TO</div>
      <div class="bill-name">${inv.clientName || 'Client'}</div>
      ${inv.clientEmail ? '<div class="bill-detail">'+inv.clientEmail+'</div>' : ''}
      ${inv.clientAddress ? '<div class="bill-detail">'+inv.clientAddress+'</div>' : ''}
    </div>

    <table>
      <tr><th>Detail</th><th style="text-align:right">Value</th></tr>
      <tr><td>Description</td><td style="text-align:right">${inv.description || '—'}</td></tr>
      <tr><td>Date Issued</td><td style="text-align:right">${inv.dateCreated || '—'}</td></tr>
      <tr><td>Due Date</td><td style="text-align:right">${inv.dueDate || '—'}</td></tr>
      <tr><td>Payment Terms</td><td style="text-align:right">${cp.payment_terms || '30'} days</td></tr>
      <tr><td>VAT Rate</td><td style="text-align:right">${vatRate}%</td></tr>
    </table>

    <div class="summary">
      <div class="sum-row"><span>Subtotal</span><span style="font-weight:600">£${subtotal.toLocaleString('en-GB',{minimumFractionDigits:2})}</span></div>
      <div class="sum-row"><span>VAT (${vatRate}%)</span><span style="font-weight:600">£${vat.toLocaleString('en-GB',{minimumFractionDigits:2})}</span></div>
      <div class="sum-row sum-total"><span class="sum-label">TOTAL DUE</span><span class="sum-val">£${total.toLocaleString('en-GB',{minimumFractionDigits:2})}</span></div>
    </div>

    ${coBank ? '<div class="bank"><div class="bank-title">PAYMENT DETAILS</div>'
      +'<div class="bank-row"><span class="bank-label">Bank</span><span class="bank-val">'+coBank+'</span></div>'
      +(coAcct?'<div class="bank-row"><span class="bank-label">Account</span><span class="bank-val">'+coAcct+'</span></div>':'')
      +(coSort?'<div class="bank-row"><span class="bank-label">Sort Code</span><span class="bank-val">'+coSort+'</span></div>':'')
      +(coIBAN?'<div class="bank-row"><span class="bank-label">IBAN</span><span class="bank-val">'+coIBAN+'</span></div>':'')
      +'</div>' : ''}

    ${cp.invoice_footer ? '<div class="notes">'+cp.invoice_footer+'</div>' : ''}

    <div class="footer">
      <strong>${coName}</strong><br/>
      ${coEmail}${cp.website ? ' | '+cp.website : ''}
      ${coVAT ? '<br/>VAT: '+coVAT : ''}${coReg ? ' | Company Reg: '+coReg : ''}
    </div>
    </body></html>`;
  };

  const generateInvoicePDF = async (inv, action = 'share') => {
    try {
      const html = generateInvoiceHTML(inv);
      const { uri } = await Print.printToFileAsync({ html, base64: false });
      const filename = (inv.invoiceNumber || 'invoice').replace(/[^a-zA-Z0-9-]/g, '_') + '.pdf';

      if (action === 'share' || action === 'download') {
        if (await Sharing.isAvailableAsync()) {
          await Sharing.shareAsync(uri, {
            mimeType: 'application/pdf',
            dialogTitle: 'Share Invoice ' + (inv.invoiceNumber || ''),
            UTI: 'com.adobe.pdf',
          });
        } else {
          Alert.alert('PDF Ready', 'Invoice PDF generated: ' + filename);
        }
      } else if (action === 'print') {
        await Print.printAsync({ uri });
      } else if (action === 'email') {
        if (await Sharing.isAvailableAsync()) {
          await Sharing.shareAsync(uri, {
            mimeType: 'application/pdf',
            dialogTitle: 'Email Invoice to ' + (inv.clientEmail || 'client'),
            UTI: 'com.adobe.pdf',
          });
        }
      }
    } catch (e) {
      Alert.alert('Error', 'Failed to generate PDF: ' + e.message);
    }
  };

  // ─── Expense CRUD ───────────────────────────────────────────────────────────
  const handleCreateExpense = async () => {
    if (!newExpense.description || !newExpense.amount) {
      Alert.alert('Error', 'Description and amount are required'); return;
    }
    const exp = { ...newExpense, company_id:businessId, id:`exp_${Date.now()}`, amount:parseFloat(newExpense.amount), vatReclaim:parseFloat(newExpense.vatReclaim||0) };
    setExpenses(p => [...p, exp]);
    setShowModal(false); setNewExpense(blankExpense);
    Alert.alert('Success', 'Expense added');
    try {
      const res = await finApi.post('/api/data/financial_expenses', exp);
      const saved = res.data?.data ?? res.data;
      if (saved?.id && saved.id !== exp.id) setExpenses(p => p.map(e => e.id === exp.id ? {...e, id:saved.id} : e));
      await AsyncStorage.removeItem(`fin_exp_${businessId}`);
    } catch (e) { console.warn('[FinAPI] POST expense:', e.message); }
  };

  const handleUpdateExpense = async (id, updates) => {
    setExpenses(p => p.map(e => e.id === id ? {...e, ...updates} : e));
    try {
      await finApi.put(EP.expense(id), updates);
      await AsyncStorage.removeItem(`fin_exp_${businessId}`);
    } catch (e) { console.warn('[FinAPI] PUT expense:', e.message); }
  };

  // ─── Budget CRUD ────────────────────────────────────────────────────────────
  const handleCreateBudget = async () => {
    if (!newBudget.category || !newBudget.allocated) {
      Alert.alert('Error', 'Category and allocated amount required'); return;
    }
    const bud = { ...newBudget, company_id:businessId, id:`bud_${Date.now()}`, allocated:parseFloat(newBudget.allocated) };
    setBudgets(p => [...p, bud]);
    setShowModal(false); setNewBudget(blankBudget);
    Alert.alert('Success', 'Budget created');
    try {
      const res = await finApi.post('/api/data/financial_budgets', bud);
      const saved = res.data?.data ?? res.data;
      if (saved?.id && saved.id !== bud.id) setBudgets(p => p.map(b => b.id === bud.id ? {...b, id:saved.id} : b));
      await AsyncStorage.removeItem(`fin_bud_${businessId}`);
    } catch (e) { console.warn('[FinAPI] POST budget:', e.message); }
  };

  // ─── Tax Obligation CRUD ────────────────────────────────────────────────────
  const handleCreateTax = async () => {
    if (!newTax.type || !newTax.dueDate) {
      Alert.alert('Error', 'Type and due date required'); return;
    }
    const tax = { ...newTax, company_id:businessId, id:`tax_${Date.now()}`, amount:parseFloat(newTax.amount||0) };
    setTaxObligations(p => [...p, tax]);
    setShowModal(false); setNewTax(blankTax);
    Alert.alert('Success', 'Tax obligation added');
    try {
      const res = await finApi.post('/api/data/financial_tax_obligations', tax);
      const saved = res.data?.data ?? res.data;
      if (saved?.id && saved.id !== tax.id) setTaxObligations(p => p.map(t => t.id === tax.id ? {...t, id:saved.id} : t));
      await AsyncStorage.removeItem(`fin_tax_${businessId}`);
    } catch (e) { console.warn('[FinAPI] POST tax:', e.message); }
  };

  const handleUpdateTaxStatus = async (id, status) => {
    setTaxObligations(p => p.map(t => t.id === id ? {...t, status} : t));
    try {
      await finApi.put(EP.taxObligation(id), { status });
      await AsyncStorage.removeItem(`fin_tax_${businessId}`);
    } catch (e) { console.warn('[FinAPI] PUT tax status:', e.message); }
  };

  // ─── Bank Account CRUD ──────────────────────────────────────────────────────
  const handleCreateBankAccount = async () => {
    if (!newBank.accountName || !newBank.bankName) {
      Alert.alert('Error', 'Account name and bank name required'); return;
    }
    const acct = { ...newBank, company_id:businessId, id:`bank_${Date.now()}`, balance:parseFloat(newBank.balance||0) };
    setBankAccounts(p => [...p, acct]);
    setShowModal(false); setNewBank(blankBank);
    Alert.alert('Success', 'Bank account added');
    try {
      const res = await finApi.post('/api/data/financial_bank_accounts', acct);
      const saved = res.data?.data ?? res.data;
      if (saved?.id && saved.id !== acct.id) setBankAccounts(p => p.map(a => a.id === acct.id ? {...a, id:saved.id} : a));
      await AsyncStorage.removeItem(`fin_bank_${businessId}`);
    } catch (e) { console.warn('[FinAPI] POST bank account:', e.message); }
  };

  const handleSetDefaultAccount = async (id) => {
    setBankAccounts(p => p.map(a => ({...a, isDefault: a.id === id})));
    try { await finApi.put(EP.bankAccount(id), { is_default:true }); } catch (_) {}
  };

  // ─── Generic Delete ─────────────────────────────────────────────────────────
  const handleDelete = (id, collection, setter, endpoint, cacheKey) => {
    Alert.alert('Delete', 'Are you sure you want to delete this item?', [
      { text:'Cancel', style:'cancel' },
      { text:'Delete', style:'destructive', onPress: async () => {
        setter(p => p.filter(i => i.id !== id));
        try {
          await finApi.delete(endpoint(id));
          await AsyncStorage.removeItem(cacheKey);
        } catch (e) { console.warn('[FinAPI] DELETE:', e.message); }
      }},
    ]);
  };

  // ─── Tax Calculator ─────────────────────────────────────────────────────────
  const calcVAT = (amount, rate = 20) => {
    const vatAmount = (amount * rate) / 100;
    return { netAmount: amount, vatAmount, totalAmount: amount + vatAmount };
  };

  const calcSelfEmployedTax = (profit) => {
    let incomeTax = 0, ni = 0, breakdown = [];
    const PA = UK_TAX_RATES.incomeTax.personalAllowance;
    if (profit > PA) {
      const taxable = profit - PA;
      const br = UK_TAX_RATES.incomeTax.basicRate, hr = UK_TAX_RATES.incomeTax.higherRate;
      if (taxable <= br.threshold - PA) {
        incomeTax = taxable * br.rate;
        breakdown.push(`Basic rate 20%: £${incomeTax.toFixed(2)}`);
      } else if (taxable <= hr.threshold - PA) {
        const b = (br.threshold - PA) * br.rate, h = (taxable - (br.threshold - PA)) * hr.rate;
        incomeTax = b + h; breakdown.push(`Basic 20%: £${b.toFixed(2)}`); breakdown.push(`Higher 40%: £${h.toFixed(2)}`);
      } else {
        const b = (br.threshold - PA) * br.rate, h = (hr.threshold - br.threshold) * hr.rate;
        const a = (taxable - (hr.threshold - PA)) * UK_TAX_RATES.incomeTax.additionalRate.rate;
        incomeTax = b + h + a;
        breakdown.push(`Basic 20%: £${b.toFixed(2)}`); breakdown.push(`Higher 40%: £${h.toFixed(2)}`); breakdown.push(`Additional 45%: £${a.toFixed(2)}`);
      }
    }
    const c2 = UK_TAX_RATES.nationalInsurance.class2, c4 = UK_TAX_RATES.nationalInsurance.class4;
    if (profit >= c2.smallProfitsThreshold) {
      const c2a = c2.rate * 52; ni += c2a; breakdown.push(`Class 2 NI: £${c2a.toFixed(2)}`);
      if (profit > c4.lowerProfitsLimit) {
        const c4p = Math.min(profit - c4.lowerProfitsLimit, c4.upperProfitsLimit - c4.lowerProfitsLimit);
        const c4a = c4p * c4.rate; ni += c4a; breakdown.push(`Class 4 NI 9%: £${c4a.toFixed(2)}`);
        if (profit > c4.upperProfitsLimit) { const h = (profit - c4.upperProfitsLimit) * c4.higherRate; ni += h; breakdown.push(`Class 4 NI 2%: £${h.toFixed(2)}`); }
      }
    }
    const total = incomeTax + ni;
    return { grossIncome:profit, netIncome:profit, incomeTax, nationalInsurance:ni, totalTax:total, netAfterTax:profit-total, breakdown, personalAllowance:PA };
  };

  const calcEmployedTax = (salary) => {
    let incomeTax = 0, ni = 0, breakdown = [];
    const PA = UK_TAX_RATES.incomeTax.personalAllowance;
    if (salary > PA) {
      const taxable = salary - PA, br = UK_TAX_RATES.incomeTax.basicRate, hr = UK_TAX_RATES.incomeTax.higherRate;
      if (taxable <= br.threshold - PA) { incomeTax = taxable * br.rate; breakdown.push(`Basic 20%: £${incomeTax.toFixed(2)}`); }
      else { const b = (br.threshold - PA) * br.rate, h = (taxable - (br.threshold - PA)) * hr.rate; incomeTax = b + h; breakdown.push(`Basic 20%: £${b.toFixed(2)}`); breakdown.push(`Higher 40%: £${h.toFixed(2)}`); }
    }
    const c1 = UK_TAX_RATES.nationalInsurance.class1;
    if (salary > c1.lowerEarningsLimit) {
      const niable = Math.min(salary - c1.lowerEarningsLimit, c1.upperEarningsLimit - c1.lowerEarningsLimit);
      ni = niable * c1.rate; breakdown.push(`Class 1 NI 12%: £${ni.toFixed(2)}`);
      if (salary > c1.upperEarningsLimit) { const h = (salary - c1.upperEarningsLimit) * c1.higherRate; ni += h; breakdown.push(`Class 1 NI 2%: £${h.toFixed(2)}`); }
    }
    const total = incomeTax + ni;
    return { grossIncome:salary, netIncome:salary, incomeTax, nationalInsurance:ni, totalTax:total, netAfterTax:salary-total, breakdown, personalAllowance:PA };
  };

  const calcCorporationTax = (profit) => {
    let ct = 0, breakdown = [];
    if (profit <= 50000) { ct = profit * UK_TAX_RATES.corporationTax.smallCompaniesRate; breakdown.push(`Small companies 19%: £${ct.toFixed(2)}`); }
    else if (profit <= 250000) { ct = profit * UK_TAX_RATES.corporationTax.marginalRate; breakdown.push(`Marginal 26.25%: £${ct.toFixed(2)}`); }
    else { ct = profit * UK_TAX_RATES.corporationTax.mainRate; breakdown.push(`Main rate 25%: £${ct.toFixed(2)}`); }
    return { grossIncome:profit, netIncome:profit, incomeTax:0, nationalInsurance:0, corporationTax:ct, totalTax:ct, netAfterTax:profit-ct, breakdown };
  };

  const runTaxCalc = () => {
    if (!taxCalc.income) { Alert.alert('Error','Please enter income'); return; }
    const income = parseFloat(taxCalc.income), expVal = parseFloat(taxCalc.expenses||0);
    const net = income - expVal;
    let results;
    if (taxCalc.employmentStatus === 'selfEmployed') results = calcSelfEmployedTax(net);
    else if (taxCalc.employmentStatus === 'employed') results = calcEmployedTax(net);
    else results = calcCorporationTax(net);
    results.allowableExpenses = expVal;
    setTaxCalc(p => ({...p, results}));
    saveTaxCalcToAPI(results);
  };

  const saveTaxCalcToAPI = async (results) => {
    const record = { company_id:businessId, income:taxCalc.income, expenses:taxCalc.expenses, employment_status:taxCalc.employmentStatus, income_tax:results.incomeTax, national_insurance:results.nationalInsurance, total_tax:results.totalTax, net_after_tax:results.netAfterTax, calculated_at:new Date().toISOString() };
    setTaxCalcHistory(p => [record, ...p.slice(0,19)]);
    try { await finApi.post(EP.taxCalcHistory(businessId), record); await AsyncStorage.removeItem(`fin_taxhist_${businessId}`); } catch (_) {}
  };

  // ─── Reports ────────────────────────────────────────────────────────────────
  const buildReportData = (type) => {
    const cap = s => s.charAt(0).toUpperCase() + s.slice(1);
    switch (type) {
      case 'Income Statement': {
        const rev = invoices.filter(i => i.status==='paid').reduce((s,i) => s + i.amount, 0);
        const exp = expenses.reduce((s,e) => s + e.amount, 0);
        return { title:'Income Statement', period:`${cap(reportPeriod)} Report`,
          data:[{label:'Total Revenue',value:`£${rev.toLocaleString()}`,type:'revenue'},{label:'Total Expenses',value:`£${exp.toLocaleString()}`,type:'expense'},{label:'Net Profit',value:`£${(rev-exp).toLocaleString()}`,type:'profit'}],
          details: invoices.filter(i=>i.status==='paid').map(i=>({date:i.dateCreated,description:`${i.invoiceNumber} - ${i.clientName}`,amount:i.amount,type:'revenue'})).concat(expenses.map(e=>({date:e.date,description:e.description,amount:-e.amount,type:'expense'})))};
      }
      case 'Cash Flow Statement': {
        const inc = invoices.filter(i=>i.status==='paid').reduce((s,i)=>s+i.totalAmount,0);
        const out = expenses.reduce((s,e)=>s+e.amount,0);
        return { title:'Cash Flow Statement', period:`${cap(reportPeriod)} Report`,
          data:[{label:'Cash Inflows',value:`£${inc.toLocaleString()}`,type:'inflow'},{label:'Cash Outflows',value:`£${out.toLocaleString()}`,type:'outflow'},{label:'Net Cash Flow',value:`£${(inc-out).toLocaleString()}`,type:inc-out>=0?'positive':'negative'}], details:[] };
      }
      case 'Balance Sheet': {
        const assets = overview.cashFlow + overview.outstandingInvoices;
        const liab   = overview.vatOwed + overview.incomeTaxOwed;
        return { title:'Balance Sheet', period:`As of ${new Date().toLocaleDateString()}`,
          data:[{label:'Total Assets',value:`£${assets.toLocaleString()}`,type:'asset'},{label:'  Cash',value:`£${overview.cashFlow.toLocaleString()}`,type:'asset',indent:true},{label:'  Receivables',value:`£${overview.outstandingInvoices.toLocaleString()}`,type:'asset',indent:true},{label:'Total Liabilities',value:`£${liab.toLocaleString()}`,type:'liability'},{label:'  VAT Owed',value:`£${overview.vatOwed.toLocaleString()}`,type:'liability',indent:true},{label:'  Tax Owed',value:`£${overview.incomeTaxOwed.toLocaleString()}`,type:'liability',indent:true},{label:'Total Equity',value:`£${(assets-liab).toLocaleString()}`,type:'equity'}], details:[] };
      }
      case 'VAT Report': {
        const vatC = invoices.reduce((s,i)=>s+(i.vatAmount||0),0);
        const vatP = expenses.reduce((s,e)=>s+(e.vatReclaim||0),0);
        return { title:'VAT Report', period:`${cap(reportPeriod)} Report`,
          data:[{label:'VAT Collected',value:`£${vatC.toLocaleString()}`,type:'collected'},{label:'VAT Reclaimable',value:`£${vatP.toLocaleString()}`,type:'paid'},{label:'Net VAT Due',value:`£${(vatC-vatP).toLocaleString()}`,type:'owed'}],
          details: invoices.map(i=>({date:i.dateCreated,description:`Invoice ${i.invoiceNumber}`,vatAmount:i.vatAmount,type:'collected'})) };
      }
      case 'Expense Analysis': {
        const byCat = expenses.reduce((a,e)=>{a[e.category]=(a[e.category]||0)+e.amount;return a;},{});
        return { title:'Expense Analysis', period:`${cap(reportPeriod)} Report`,
          data: Object.entries(byCat).map(([cat,amt])=>({label:cat,value:`£${amt.toLocaleString()}`,type:'expense'})),
          details: expenses.map(e=>({date:e.date,description:e.description,category:e.category,amount:e.amount})) };
      }
      case 'Tax Summary': {
        const pend = taxObligations.filter(t=>t.status==='pending').reduce((s,t)=>s+t.amount,0);
        const done = taxObligations.filter(t=>t.status==='completed').reduce((s,t)=>s+t.amount,0);
        return { title:'Tax Summary', period:`${cap(reportPeriod)} Report`,
          data:[{label:'Pending',value:`£${pend.toLocaleString()}`,type:'pending'},{label:'Completed',value:`£${done.toLocaleString()}`,type:'completed'},{label:'Total',value:`£${(pend+done).toLocaleString()}`,type:'total'}],
          details: taxObligations.map(t=>({date:t.dueDate,description:`${t.type} - ${t.description}`,amount:t.amount,status:t.status})) };
      }
      case 'Aged Receivables': {
        return { title:'Aged Receivables', period:`As of ${new Date().toLocaleDateString()}`,
          data: agedReceivables.length > 0
            ? agedReceivables.map(r=>({label:r.label||r.bucket||r.age_bucket,value:`£${(r.amount||r.total||0).toLocaleString()}`,type:'asset'}))
            : [
                {label:'0–30 days',value:`£${invoices.filter(i=>i.status!=='paid').filter(i=>{const d=new Date(i.dueDate);return (Date.now()-d.getTime())<=30*86400000;}).reduce((s,i)=>s+i.totalAmount,0).toLocaleString()}`,type:'asset'},
                {label:'31–60 days',value:`£${invoices.filter(i=>i.status!=='paid').filter(i=>{const d=new Date(i.dueDate);const age=(Date.now()-d.getTime())/86400000;return age>30&&age<=60;}).reduce((s,i)=>s+i.totalAmount,0).toLocaleString()}`,type:'warning'},
                {label:'60+ days',value:`£${invoices.filter(i=>i.status!=='paid').filter(i=>{const d=new Date(i.dueDate);return (Date.now()-d.getTime())>60*86400000;}).reduce((s,i)=>s+i.totalAmount,0).toLocaleString()}`,type:'liability'},
              ],
          details: invoices.filter(i=>i.status!=='paid').map(i=>({date:i.dueDate,description:`${i.invoiceNumber} - ${i.clientName}`,amount:i.totalAmount,status:i.status})) };
      }
      default: return { title:'Report', data:[], details:[] };
    }
  };

  const generateReportHTML = (rd) => {
    const date = new Date().toLocaleDateString();
    const colorFor = (t) => ['revenue','profit','inflow','positive','asset','collected','inflow'].includes(t) ? '#10B981' : ['expense','outflow','negative','liability','pending'].includes(t) ? '#EF4444' : '#667eea';
    return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${rd.title}</title><style>
      body{font-family:Arial,sans-serif;margin:40px;color:#333;line-height:1.6}
      .hdr{text-align:center;margin-bottom:40px;border-bottom:2px solid #667eea;padding-bottom:20px}
      .co{font-size:24px;font-weight:bold;color:#667eea;margin-bottom:10px}
      h2{font-size:20px;margin-bottom:5px}
      .sub{font-size:14px;color:#666}
      table{width:100%;border-collapse:collapse;margin:30px 0;box-shadow:0 2px 4px rgba(0,0,0,.1)}
      th,td{padding:12px 15px;text-align:left;border-bottom:1px solid #ddd}
      th{background:#667eea;color:#fff;font-weight:bold}
      tr:hover{background:#f5f5f5}
      .pos{color:#10B981;font-weight:bold} .neg{color:#EF4444;font-weight:bold} .neu{color:#333;font-weight:bold}
      .indent{padding-left:30px}
      .footer{margin-top:50px;text-align:center;font-size:12px;color:#666;border-top:1px solid #ddd;padding-top:20px}
    </style></head><body>
      <div class="hdr">${companyLogo ? '<img src="' + companyLogo + '" style="width:50px;height:50px;border-radius:8px;margin-bottom:10px;" />' : ''}<div class="co">${companyName || 'Financial Management'}</div><h2>${rd.title}</h2><div class="sub">${rd.period} &bull; Generated ${date}</div></div>
      <table><thead><tr><th>Description</th><th>Amount</th></tr></thead><tbody>
      ${(rd.data||[]).map(i=>`<tr><td ${i.indent?'class="indent"':''}>${i.label}</td><td class="${colorFor(i.type)==='#10B981'?'pos':colorFor(i.type)==='#EF4444'?'neg':'neu'}">${i.value}</td></tr>`).join('')}
      </tbody></table>
      ${rd.details&&rd.details.length>0?`<h3>Transaction Details</h3><table><thead><tr>${rd.details[0].date?'<th>Date</th>':''}<th>Description</th>${rd.details[0].amount!==undefined?'<th>Amount</th>':''}${rd.details[0].vatAmount!==undefined?'<th>VAT</th>':''}${rd.details[0].status?'<th>Status</th>':''}</tr></thead><tbody>
      ${rd.details.map(d=>`<tr>${d.date?`<td>${d.date}</td>`:''}<td>${d.description||d.type||''}</td>${d.amount!==undefined?`<td class="${d.amount>=0?'pos':'neg'}">£${Math.abs(d.amount).toLocaleString()}</td>`:''}${d.vatAmount!==undefined?`<td>£${d.vatAmount.toLocaleString()}</td>`:''}${d.status?`<td>${d.status}</td>`:''}</tr>`).join('')}
      </tbody></table>`:''}
      <div class="footer"><p>JobEng Financial Management System &bull; Confidential</p></div>
    </body></html>`;
  };

  const handleGenerateReport = async () => {
    if (!reportType) { Alert.alert('Error','Select a report type'); return; }
    try {
      const rd = buildReportData(reportType);
      const html = generateReportHTML(rd);
      const { uri } = await Print.printToFileAsync({ html, base64:false });
      if (await Sharing.isAvailableAsync()) {
        await Sharing.shareAsync(uri, { mimeType:'application/pdf', dialogTitle:`Share ${reportType}`, UTI:'com.adobe.pdf' });
      } else {
        Alert.alert('Success', `${reportType} generated`);
      }
      setShowModal(false);
      // Save to API
      try {
        const rpt = { company_id:businessId, report_type:reportType, period:reportPeriod, generated_at:new Date().toISOString() };
        await finApi.post(EP.reports(businessId), rpt);
        setReports(p => [rpt, ...p]);
        await AsyncStorage.removeItem(`fin_rpt_${businessId}`);
      } catch (_) {}
    } catch (err) {
      Alert.alert('Error','Failed to generate report');
    }
  };

  // ─── Filters ────────────────────────────────────────────────────────────────
  const filteredInvoices = invoices.filter(i => {
    const q = searchQuery.toLowerCase();
    const matchQ = (i.clientName||'').toLowerCase().includes(q) || (i.invoiceNumber||'').toLowerCase().includes(q);
    const matchF = invoiceFilter === 'all' || i.status === invoiceFilter;
    return matchQ && matchF;
  });

  const filteredExpenses = expenseCategory
    ? expenses.filter(e => (e.category||'').toLowerCase() === expenseCategory.toLowerCase())
    : expenses;

  const filteredTaxObs = taxFilter === 'all' ? taxObligations
    : taxObligations.filter(t => t.status === taxFilter);

  // ─── Render: Header ─────────────────────────────────────────────────────────
  const renderHeader = () => (
    <LinearGradient colors={['#667eea','#764ba2']} style={S.header}>
      <SafeAreaView>
        <View style={S.headerContent}>
          <TouchableOpacity style={S.headerBtn} onPress={() => navigation.goBack()}>
            <Ionicons name="arrow-back" size={24} color="#fff" />
          </TouchableOpacity>
          <View style={S.headerCenter}>
            {companyLogo ? (
                <TouchableOpacity onPress={() => navigation?.navigate('CompanyProfileScreen')}>
                  <Image source={{ uri: companyLogo || undefined }} style={{ width: 34, height: 34, borderRadius: 8 }} resizeMode="cover" />
                </TouchableOpacity>
              ) : null}
              <Text style={S.headerTitle}>Financial Management</Text>
            <Text style={S.headerSub}>
              <Text>{offlineMode ? '⚠ Offline Mode' : 'UK Tax & Business Finance'}</Text>
            </Text>
          </View>
          <TouchableOpacity style={S.headerBtn} onPress={() => loadAll(true)}>
            <Ionicons name="refresh" size={24} color="#fff" />
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    </LinearGradient>
  );

  // ─── Render: Tab Bar ────────────────────────────────────────────────────────
  const TABS = [
    { key:'overview',  icon:'analytics',      title:'Overview'  },
    { key:'invoices',  icon:'receipt',         title:'Invoices'  },
    { key:'expenses',  icon:'card',            title:'Expenses'  },
    { key:'budgets',   icon:'wallet',          title:'Budgets'   },
    { key:'taxes',     icon:'calculator',      title:'UK Taxes'  },
    { key:'banking',   icon:'business',        title:'Banking'   },
    { key:'analytics', icon:'bar-chart',       title:'Analytics' },
    { key:'reports',   icon:'document-text',   title:'Reports'   },
    { key:'cashflow', icon:'swap-horizontal', title:'Cash Flow' },
    { key:'forecast', icon:'trending-up',     title:'Forecast'  },
  ];

  const renderTabBar = () => (
    <ScrollView horizontal showsHorizontalScrollIndicator={false} style={S.tabBar} contentContainerStyle={S.tabBarContent}>
      {TABS.map(tab => (
        <TouchableOpacity key={tab.key} style={[S.tabItem, activeTab===tab.key && S.tabItemActive]} onPress={() => setActiveTab(tab.key)}>
          <Ionicons name={tab.icon} size={18} color={activeTab===tab.key ? '#667eea' : '#888'} />
          <Text style={[S.tabText, activeTab===tab.key && S.tabTextActive]}>{tab.title}</Text>
        </TouchableOpacity>
      ))}
    </ScrollView>
  );

  // ─── Render: Overview Tab ───────────────────────────────────────────────────
  const renderOverview = () => {
    const metrics = [
      { label:'Revenue',     value:overview.totalRevenue,        icon:'trending-up',   color:'#10B981' },
      { label:'Expenses',    value:overview.totalExpenses,       icon:'trending-down',  color:'#EF4444' },
      { label:'Net Profit',  value:overview.netProfit,           icon:'cash',           color:'#3B82F6' },
      { label:'Outstanding', value:overview.outstandingInvoices, icon:'time',           color:'#F59E0B' },
      { label:'Cash Flow',   value:overview.cashFlow,            icon:'swap-horizontal',color:'#8B5CF6' },
      { label:'VAT Owed',    value:overview.vatOwed,             icon:'receipt',        color:'#EF4444' },
      { label:'Tax Owed',    value:overview.incomeTaxOwed,       icon:'document',       color:'#F59E0B' },
      { label:'NI',          value:overview.niContributions,     icon:'people',         color:'#6366F1' },
    ];
    const quickActions = [
      { label:'New Invoice',   icon:'add-circle',    color:'#10B981', modal:'createInvoice' },
      { label:'Add Expense',   icon:'remove-circle', color:'#EF4444', modal:'addExpense'    },
      { label:'Tax Calc',      icon:'calculator',    color:'#3B82F6', modal:'taxCalculator'  },
      { label:'Add Budget',    icon:'wallet',        color:'#8B5CF6', modal:'createBudget'  },
      { label:'Bank Account',  icon:'business',      color:'#F59E0B', modal:'addBank'       },
      { label:'Add Tax',       icon:'document',      color:'#EF4444', modal:'createTax'     },
      { label:'Report',        icon:'document-text', color:'#6366F1', modal:'generateReport' },
    ];
    return (
      <ScrollView refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => loadAll(true)} />}>
        <View style={S.metricsGrid}>
          {metrics.map(m => (
            <View key={m.label} style={S.metricCard}>
              <Ionicons name={m.icon} size={22} color={m.color} />
              <Text style={S.metricVal}>£{(m.value||0).toLocaleString()}</Text>
              <Text style={S.metricLabel}>{m.label}</Text>
            </View>
          ))}
        </View>
        <Text style={S.sectionTitle}>Quick Actions</Text>
        <View style={S.qaGrid}>
          {quickActions.map(a => (
            <TouchableOpacity key={a.label} style={S.qaCard} onPress={() => { setModalType(a.modal); setShowModal(true); }}>
              <Ionicons name={a.icon} size={28} color={a.color} />
              <Text style={S.qaLabel}>{a.label}</Text>
            </TouchableOpacity>
          ))}
        </View>
        <Text style={S.sectionTitle}>Recent Invoices</Text>
        {invoices.slice(0,5).map(inv => (
          <TouchableOpacity key={inv.id} style={S.rowCard} onPress={() => { setSelectedItem(inv); setModalType('invoiceDetails'); setShowModal(true);  }}>
            <View style={{flex:1}}>
              <Text style={S.rowTitle}>{inv.clientName}</Text>
              <Text style={S.rowSub}>{inv.invoiceNumber} · Due {inv.dueDate}</Text>
            </View>
            <View style={{alignItems:'flex-end'}}>
              <Text style={S.rowAmount}>£{(inv.totalAmount||0).toLocaleString()}</Text>
              <View style={[S.badge, S[`badge_${inv.status}`]]}><Text style={S.badgeText}>{(inv.status||'').toUpperCase()}</Text></View>
            </View>
          </TouchableOpacity>
        ))}
        <Text style={[S.sectionTitle,{marginTop:16}]}>Upcoming Tax Deadlines</Text>
        {taxObligations.filter(t=>t.status==='pending').slice(0,3).map(t => {
          const overdue = new Date(t.dueDate) < new Date();
          return (
            <View key={t.id} style={[S.rowCard, overdue && S.overdueCard]}>
              <View style={{flex:1}}>
                <Text style={S.rowTitle}>{t.type}</Text>
                <Text style={[S.rowSub, overdue && {color:'#EF4444'}]}>Due {t.dueDate}{overdue?' · OVERDUE':''}</Text>
              </View>
              <Text style={[S.rowAmount,{color:'#EF4444'}]}>£{(t.amount||0).toLocaleString()}</Text>
            </View>
          );
        })}
      </ScrollView>
    );
  };

  // ─── Render: Invoices Tab ───────────────────────────────────────────────────
  const renderInvoices = () => (
    <View style={{flex:1}}>
      <View style={S.filterRow}>
        <View style={S.searchBox}>
          <Ionicons name="search" size={18} color="#888" />
          <TextInput style={S.searchInput} placeholder="Search invoices…" value={searchQuery} onChangeText={setSearchQuery} />
        </View>
      </View>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={S.chipRow}>
        {['all','draft','sent','paid','overdue'].map(f => (
          <TouchableOpacity key={f} style={[S.chip, invoiceFilter===f && S.chipActive]} onPress={() => setInvoiceFilter(f)}>
            <Text style={[S.chipText, invoiceFilter===f && S.chipTextActive]}>{f.charAt(0).toUpperCase()+f.slice(1)}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
      <TouchableOpacity style={S.createBtn} onPress={() => { setModalType('createInvoice'); setShowModal(true); }}>
        <Ionicons name="add" size={20} color="#fff" /><Text style={S.createBtnText}>New Invoice</Text>
      </TouchableOpacity>
      <FlatList
        data={filteredInvoices}
        keyExtractor={i => i.id}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => loadAll(true)} />}
        renderItem={({item: inv}) => (
          <View style={S.card}>
            <View style={S.cardHeader}>
              <View style={{flex:1}}>
                <Text style={S.cardTitle}>{inv.invoiceNumber}</Text>
                <Text style={S.cardSub}>{inv.clientName}</Text>
              </View>
              <View style={{alignItems:'flex-end'}}>
                <Text style={S.bigAmount}>£{(inv.totalAmount||0).toLocaleString()}</Text>
                <View style={[S.badge, S[`badge_${inv.status}`]]}><Text style={S.badgeText}>{(inv.status||'').toUpperCase()}</Text></View>
              </View>
            </View>
            <Text style={S.cardDetail}>{inv.description}</Text>
            <Text style={S.cardMeta}>Due: {inv.dueDate} · Net: £{(inv.amount||0).toLocaleString()} · VAT: £{(inv.vatAmount||0).toLocaleString()}</Text>
            <View style={S.actionRow}>
              <TouchableOpacity style={S.actionBtn} onPress={() => { setSelectedItem(inv); setModalType('invoiceDetails'); setShowModal(true);  }}>
                <Ionicons name="eye" size={15} color="#3B82F6" /><Text style={S.actionBtnText}>View</Text>
              </TouchableOpacity>
              {inv.status !== 'paid' && (
                <TouchableOpacity style={S.actionBtn} onPress={() => handleUpdateInvoiceStatus(inv.id,'paid')}>
                  <Ionicons name="checkmark-circle" size={15} color="#10B981" /><Text style={S.actionBtnText}>Paid</Text>
                </TouchableOpacity>
              )}
              {inv.status === 'draft' && (
                <TouchableOpacity style={S.actionBtn} onPress={() => handleUpdateInvoiceStatus(inv.id,'sent')}>
                  <Ionicons name="send" size={15} color="#8B5CF6" /><Text style={S.actionBtnText}>Send</Text>
                </TouchableOpacity>
              )}
              <TouchableOpacity style={S.actionBtn} onPress={() => handleDelete(inv.id, invoices, setInvoices, EP.invoice, `fin_inv_${businessId}`)}>
                <Ionicons name="trash" size={15} color="#EF4444" /><Text style={S.actionBtnText}>Delete</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}
        ListEmptyComponent={<Text style={S.emptyText}>No invoices found</Text>}
        contentContainerStyle={{paddingBottom:20}}
      />
    </View>
  );

  // ─── Render: Expenses Tab ───────────────────────────────────────────────────
  const renderExpenses = () => (
    <View style={{flex:1}}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={S.chipRow}>
        <TouchableOpacity style={[S.chip, !expenseCategory && S.chipActive]} onPress={() => setExpenseCategory('')}>
          <Text style={[S.chipText, !expenseCategory && S.chipTextActive]}>All</Text>
        </TouchableOpacity>
        {EXPENSE_CATEGORIES.map(cat => (
          <TouchableOpacity key={cat} style={[S.chip, expenseCategory===cat && S.chipActive]} onPress={() => setExpenseCategory(cat)}>
            <Text style={[S.chipText, expenseCategory===cat && S.chipTextActive]}>{cat}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
      <TouchableOpacity style={S.createBtn} onPress={() => { setModalType('addExpense'); setShowModal(true); }}>
        <Ionicons name="add" size={20} color="#fff" /><Text style={S.createBtnText}>Add Expense</Text>
      </TouchableOpacity>
      <View style={S.summaryRow}>
        <View style={S.summaryCard}>
          <Text style={S.summaryLabel}>Total</Text>
          <Text style={S.summaryVal}>£{filteredExpenses.reduce((s,e)=>s+e.amount,0).toLocaleString()}</Text>
        </View>
        <View style={S.summaryCard}>
          <Text style={S.summaryLabel}>VAT Reclaimable</Text>
          <Text style={[S.summaryVal,{color:'#10B981'}]}>£{filteredExpenses.reduce((s,e)=>s+(e.vatReclaim||0),0).toLocaleString()}</Text>
        </View>
      </View>
      <FlatList
        data={filteredExpenses}
        keyExtractor={e => e.id}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => loadAll(true)} />}
        renderItem={({item: exp}) => (
          <View style={S.card}>
            <View style={S.cardHeader}>
              <View style={{flex:1}}>
                <Text style={S.cardTitle}>{exp.description}</Text>
                <Text style={S.cardSub}>{exp.category} · {exp.date}</Text>
              </View>
              <View style={{alignItems:'flex-end'}}>
                <Text style={[S.bigAmount,{color:'#EF4444'}]}>£{(exp.amount||0).toLocaleString()}</Text>
                {exp.vatReclaim > 0 && <Text style={{color:'#10B981',fontSize:12}}>VAT: £{exp.vatReclaim}</Text>}
              </View>
            </View>
            {exp.businessPurpose ? <Text style={S.cardDetail}>{exp.businessPurpose}</Text> : null}
            {exp.supplier ? <Text style={S.cardMeta}>Supplier: {exp.supplier}</Text> : null}
            <View style={S.actionRow}>
              <TouchableOpacity style={S.actionBtn} onPress={() => handleDelete(exp.id, expenses, setExpenses, EP.expense, `fin_exp_${businessId}`)}>
                <Ionicons name="trash" size={15} color="#EF4444" /><Text style={S.actionBtnText}>Delete</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}
        ListEmptyComponent={<Text style={S.emptyText}>No expenses found</Text>}
        contentContainerStyle={{paddingBottom:20}}
      />
    </View>
  );

  // ─── Render: Budgets Tab ────────────────────────────────────────────────────
  const renderBudgets = () => (
    <FlatList
      data={budgets}
      keyExtractor={b => b.id}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => loadAll(true)} />}
      ListHeaderComponent={
        <TouchableOpacity style={[S.createBtn,{margin:16}]} onPress={() => { setModalType('createBudget'); setShowModal(true); }}>
          <Ionicons name="add" size={20} color="#fff" /><Text style={S.createBtnText}>New Budget</Text>
        </TouchableOpacity>
      }
      renderItem={({item: bud}) => {
        const pct = bud.allocated > 0 ? (bud.spent / bud.allocated) * 100 : 0;
        const color = pct > 100 ? '#EF4444' : pct > 80 ? '#F59E0B' : '#10B981';
        // Look up matching perf data from API
        const perf = budgetPerf.find(p => (p.category||'').toLowerCase() === (bud.category||'').toLowerCase());
        return (
          <View style={S.card}>
            <View style={S.cardHeader}>
              <Text style={S.cardTitle}>{bud.category}</Text>
              <Text style={S.cardSub}>{bud.period}</Text>
            </View>
            <View style={S.budgetAmounts}>
              <Text style={{fontWeight:'600',color:'#1F2937'}}>£{(bud.spent||0).toLocaleString()} spent</Text>
              <Text style={{color:'#6B7280'}}>of £{(bud.allocated||0).toLocaleString()}</Text>
            </View>
            <View style={S.progressBg}>
              <View style={[S.progressFill,{width:`${Math.min(pct,100)}%`,backgroundColor:color}]} />
            </View>
            <Text style={[S.pctText,{color}]}>{pct.toFixed(1)}%</Text>
            {perf && <Text style={S.cardMeta}>Forecast: £{(perf.forecast||perf.projected||0).toLocaleString()} · Variance: {perf.variance_pct||perf.variance||'N/A'}</Text>}
            <View style={S.actionRow}>
              <TouchableOpacity style={S.actionBtn} onPress={() => { setNewExpense({...blankExpense, category:bud.category}); setModalType('addExpense'); setShowModal(true); }}>
                <Ionicons name="add" size={15} color="#3B82F6" /><Text style={S.actionBtnText}>Add Expense</Text>
              </TouchableOpacity>
              <TouchableOpacity style={S.actionBtn} onPress={() => handleDelete(bud.id, budgets, setBudgets, EP.budget, `fin_bud_${businessId}`)}>
                <Ionicons name="trash" size={15} color="#EF4444" /><Text style={S.actionBtnText}>Delete</Text>
              </TouchableOpacity>
            </View>
          </View>
        );
      }}
      ListEmptyComponent={<Text style={S.emptyText}>No budgets yet</Text>}
      contentContainerStyle={{paddingBottom:20}}
    />
  );

  // ─── Render: Taxes Tab ──────────────────────────────────────────────────────
  const renderTaxes = () => (
    <ScrollView style={{flex:1}} contentContainerStyle={{paddingBottom:40}} nestedScrollEnabled={true}>
      <View style={S.taxCalcCard}>
        <Text style={S.sectionTitle}>UK Tax Calculator 2024/25</Text>
        <TouchableOpacity style={S.createBtn} onPress={() => { setModalType('taxCalculator'); setShowModal(true); }}>
          <Ionicons name="calculator" size={20} color="#fff" /><Text style={S.createBtnText}>Open Calculator</Text>
        </TouchableOpacity>
      </View>
      <View style={S.taxRatesCard}>
        <Text style={S.sectionTitle}>Key Tax Rates 2024/25</Text>
        {[
          ['Personal Allowance','£12,570'],['Basic Rate (20%)','£12,571–£50,270'],
          ['Higher Rate (40%)','£50,271–£125,140'],['Additional Rate (45%)','Over £125,140'],
          ['Corporation Tax (small)','19% (≤£50k)'],['Corporation Tax (main)','25% (>£250k)'],
          ['Standard VAT','20%'],['VAT Registration','£85,000'],
          ['Dividend Allowance','£1,000'],['Capital Gains Exemption','£6,000'],
        ].map(([label,val]) => (
          <View key={label} style={S.taxRateRow}>
            <Text style={S.taxRateLabel}>{label}</Text>
            <Text style={S.taxRateVal}>{val}</Text>
          </View>
        ))}
      </View>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={S.chipRow}>
        {['all','pending','completed'].map(f => (
          <TouchableOpacity key={f} style={[S.chip, taxFilter===f && S.chipActive]} onPress={() => setTaxFilter(f)}>
            <Text style={[S.chipText, taxFilter===f && S.chipTextActive]}>{f.charAt(0).toUpperCase()+f.slice(1)}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
      <TouchableOpacity style={S.createBtn} onPress={() => { setModalType('createTax'); setShowModal(true); }}>
        <Ionicons name="add" size={20} color="#fff" /><Text style={S.createBtnText}>Add Tax Obligation</Text>
      </TouchableOpacity>
      {filteredTaxObs.map((tax) => {
          const overdue = new Date(tax.dueDate) < new Date() && tax.status === 'pending';
          return (
            <View key={tax.id} style={[S.card, overdue && S.overdueCard]}>
              <View style={S.cardHeader}>
                <View style={{flex:1}}>
                  <Text style={S.cardTitle}>{tax.type}</Text>
                  <Text style={S.cardSub}>{tax.description}</Text>
                </View>
                <View style={[S.badge, S[`badge_${tax.status}`]]}><Text style={S.badgeText}>{(tax.status||'').toUpperCase()}</Text></View>
              </View>
              <View style={{flexDirection:'row',justifyContent:'space-between',marginTop:8}}>
                <View><Text style={S.cardMeta}>Amount</Text><Text style={[S.bigAmount,{color:'#EF4444',fontSize:18}]}>£{(tax.amount||0).toLocaleString()}</Text></View>
                <View style={{alignItems:'flex-end'}}><Text style={S.cardMeta}>Due Date</Text><Text style={[S.cardDetail,overdue&&{color:'#EF4444'}]}>{tax.dueDate}</Text></View>
              </View>
              {tax.reference ? <Text style={S.cardMeta}>Ref: {tax.reference}</Text> : null}
              <View style={S.actionRow}>
                {tax.status === 'pending' && (
                  <TouchableOpacity style={S.actionBtn} onPress={() => handleUpdateTaxStatus(tax.id,'completed')}>
                    <Ionicons name="checkmark-circle" size={15} color="#10B981" /><Text style={S.actionBtnText}>Mark Complete</Text>
                  </TouchableOpacity>
                )}
                <TouchableOpacity style={S.actionBtn} onPress={() => handleDelete(tax.id, taxObligations, setTaxObligations, EP.taxObligation, `fin_tax_${businessId}`)}>
                  <Ionicons name="trash" size={15} color="#EF4444" /><Text style={S.actionBtnText}>Delete</Text>
                </TouchableOpacity>
              </View>
            </View>
          );
      })}
      {filteredTaxObs.length === 0 && <Text style={S.emptyText}>No tax obligations</Text>}
    </ScrollView>
  );

  // ─── Render: Banking Tab ────────────────────────────────────────────────────
  const renderBanking = () => (
    <FlatList
      data={bankAccounts}
      keyExtractor={a => a.id}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => loadAll(true)} />}
      ListHeaderComponent={
        <TouchableOpacity style={[S.createBtn,{margin:16}]} onPress={() => { setModalType('addBank'); setShowModal(true); }}>
          <Ionicons name="add" size={20} color="#fff" /><Text style={S.createBtnText}>Add Bank Account</Text>
        </TouchableOpacity>
      }
      renderItem={({item: acct}) => (
        <View style={[S.card, acct.isDefault && {borderLeftColor:'#667eea',borderLeftWidth:4}]}>
          <View style={S.cardHeader}>
            <Ionicons name="card" size={28} color="#667eea" style={{marginRight:12}} />
            <View style={{flex:1}}>
              <Text style={S.cardTitle}>{acct.accountName}</Text>
              <Text style={S.cardSub}>{acct.bankName}</Text>
            </View>
            {acct.isDefault && <View style={[S.badge,{backgroundColor:'#667eea'}]}><Text style={S.badgeText}>DEFAULT</Text></View>}
          </View>
          <View style={{flexDirection:'row',justifyContent:'space-between',marginTop:8}}>
            <View><Text style={S.cardMeta}>Account</Text><Text style={S.cardDetail}>{acct.accountNumber}</Text></View>
            <View><Text style={S.cardMeta}>Sort Code</Text><Text style={S.cardDetail}>{acct.sortCode}</Text></View>
            <View style={{alignItems:'flex-end'}}><Text style={S.cardMeta}>Balance</Text><Text style={[S.bigAmount,{fontSize:18}]}>£{(acct.balance||0).toLocaleString()}</Text></View>
          </View>
          {acct.iban ? <Text style={S.cardMeta}>IBAN: {acct.iban}</Text> : null}
          {acct.swift ? <Text style={S.cardMeta}>SWIFT: {acct.swift}</Text> : null}
          <View style={S.actionRow}>
            {!acct.isDefault && (
              <TouchableOpacity style={S.actionBtn} onPress={() => handleSetDefaultAccount(acct.id)}>
                <Ionicons name="star" size={15} color="#F59E0B" /><Text style={S.actionBtnText}>Set Default</Text>
              </TouchableOpacity>
            )}
            <TouchableOpacity style={S.actionBtn} onPress={() => handleDelete(acct.id, bankAccounts, setBankAccounts, EP.bankAccount, `fin_bank_${businessId}`)}>
              <Ionicons name="trash" size={15} color="#EF4444" /><Text style={S.actionBtnText}>Delete</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}
      ListEmptyComponent={<Text style={S.emptyText}>No bank accounts added</Text>}
      contentContainerStyle={{paddingBottom:20}}
    />
  );

  // ─── Render: Analytics Tab ──────────────────────────────────────────────────
  const renderAnalytics = () => {
    const totalExp = expenses.reduce((s,e)=>s+e.amount,0);
    const byCat = expenses.reduce((a,e)=>{a[e.category]=(a[e.category]||0)+e.amount;return a;},{});
    const catData = expByCategory.length > 0
      ? expByCategory.map(e=>({label:e.category||e.name,amount:e.total||e.amount||0}))
      : Object.entries(byCat).map(([cat,amt])=>({label:cat,amount:amt})).sort((a,b)=>b.amount-a.amount);
    return (
      <ScrollView refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => loadAll(true)} />}>
        <Text style={S.sectionTitle}>Expense by Category</Text>
        {catData.map(c => {
          const pct = totalExp > 0 ? (c.amount / totalExp) * 100 : 0;
          return (
            <View key={c.label} style={S.card}>
              <View style={{flexDirection:'row',justifyContent:'space-between'}}>
                <Text style={S.cardTitle}>{c.label || 'Uncategorised'}</Text>
                <Text style={S.bigAmount}>£{(c.amount||0).toLocaleString()}</Text>
              </View>
              <View style={S.progressBg}>
                <View style={[S.progressFill,{width:`${pct}%`,backgroundColor:'#667eea'}]} />
              </View>
              <Text style={S.cardMeta}>{pct.toFixed(1)}% of total expenses</Text>
            </View>
          );
        })}
        <Text style={S.sectionTitle}>Aged Receivables</Text>
        {(agedReceivables.length > 0 ? agedReceivables.map(r=>({label:r.label||r.bucket||r.age_bucket||'N/A',amount:r.amount||r.total||0})) : [
          {label:'0–30 days',amount:invoices.filter(i=>i.status!=='paid').filter(i=>(Date.now()-new Date(i.dueDate).getTime())<=30*86400000).reduce((s,i)=>s+i.totalAmount,0)},
          {label:'31–60 days',amount:invoices.filter(i=>i.status!=='paid').filter(i=>{const a=(Date.now()-new Date(i.dueDate).getTime())/86400000;return a>30&&a<=60;}).reduce((s,i)=>s+i.totalAmount,0)},
          {label:'60+ days',amount:invoices.filter(i=>i.status!=='paid').filter(i=>(Date.now()-new Date(i.dueDate).getTime())>60*86400000).reduce((s,i)=>s+i.totalAmount,0)},
        ]).map(r => (
          <View key={r.label} style={S.card}>
            <View style={{flexDirection:'row',justifyContent:'space-between'}}>
              <Text style={S.cardTitle}>{r.label}</Text>
              <Text style={[S.bigAmount,{color: r.label.includes('60+') ? '#EF4444' : '#F59E0B'}]}>£{(r.amount||0).toLocaleString()}</Text>
            </View>
          </View>
        ))}
        <Text style={S.sectionTitle}>Tax Calculator History</Text>
        {taxCalcHistory.slice(0,5).map((h,i) => (
          <View key={i} style={S.card}>
            <View style={{flexDirection:'row',justifyContent:'space-between'}}>
              <Text style={S.cardTitle}>{h.employment_status || 'N/A'}</Text>
              <Text style={[S.bigAmount,{fontSize:16}]}>£{(h.total_tax||0).toLocaleString()}</Text>
            </View>
            <Text style={S.cardMeta}>Income: £{(h.income||0).toLocaleString()} · Net after tax: £{(h.net_after_tax||0).toLocaleString()}</Text>
            <Text style={S.cardMeta}>{h.calculated_at ? new Date(h.calculated_at).toLocaleDateString() : ''}</Text>
          </View>
        ))}
        {taxCalcHistory.length === 0 && <Text style={S.emptyText}>No calculation history yet</Text>}
      </ScrollView>
    );
  };

  // ─── Render: Reports Tab ────────────────────────────────────────────────────
  const REPORT_TYPES = [
    { title:'Income Statement',    icon:'document-text', color:'#3B82F6', desc:'Profit & loss analysis' },
    { title:'Cash Flow Statement', icon:'bar-chart',     color:'#10B981', desc:'Cash in and out'       },
    { title:'Balance Sheet',       icon:'scale',         color:'#8B5CF6', desc:'Assets & liabilities'  },
    { title:'VAT Report',          icon:'receipt',       color:'#F59E0B', desc:'VAT for HMRC'          },
    { title:'Expense Analysis',    icon:'pie-chart',     color:'#EF4444', desc:'Spending breakdown'    },
    { title:'Tax Summary',         icon:'calculator',    color:'#6366F1', desc:'All tax obligations'   },
    { title:'Aged Receivables',    icon:'time',          color:'#F97316', desc:'Outstanding invoices'  },
  ];
  const renderReports = () => (
    <FlatList
      data={REPORT_TYPES}
      keyExtractor={r => r.title}
      numColumns={2}
      columnWrapperStyle={{justifyContent:'space-between',paddingHorizontal:16}}
      ListHeaderComponent={<Text style={[S.sectionTitle,{margin:16}]}>Financial Reports</Text>}
      renderItem={({item: r}) => (
        <TouchableOpacity style={[S.reportCard,{borderLeftColor:r.color}]} onPress={() => { setReportType(r.title); setModalType('generateReport'); setShowModal(true); }}>
          <Ionicons name={r.icon} size={28} color={r.color} />
          <Text style={S.reportTitle}>{r.title}</Text>
          <Text style={S.reportDesc}>{r.desc}</Text>
          <View style={{flexDirection:'row',alignItems:'center',marginTop:8}}>
            <Ionicons name="download-outline" size={14} color="#888" />
            <Text style={{fontSize:11,color:'#888',marginLeft:4}}>PDF Export</Text>
          </View>
        </TouchableOpacity>
      )}
      ListFooterComponent={
        reports.length > 0 ? (
          <View style={{padding:16}}>
            <Text style={S.sectionTitle}>Generated Reports</Text>
            {reports.map((r,i) => (
              <View key={i} style={S.card}>
                <Text style={S.cardTitle}>{r.report_type}</Text>
                <Text style={S.cardMeta}>{r.period} · {r.generated_at ? new Date(r.generated_at).toLocaleDateString() : ''}</Text>
              </View>
            ))}
          </View>
        ) : null
      }
      contentContainerStyle={{paddingBottom:20}}
    />
  );

  // ─── Modals ─────────────────────────────────────────────────────────────────
  const ModalShell = ({title, onSave, saveLabel='Save', children}) => (
    <Modal visible={showModal && !!modalType} animationType="slide" presentationStyle="pageSheet">
      <SafeAreaView style={S.modalShell}>
        <View style={S.modalHeader}>
          <TouchableOpacity onPress={() => { setShowModal(false); setSelectedItem(null); }} style={S.modalClose}>
            <Ionicons name="close" size={24} color="#555" />
          </TouchableOpacity>
          <Text style={S.modalTitle}>{title}</Text>
          {onSave
            ? <TouchableOpacity onPress={onSave} style={S.modalSaveBtn}><Text style={S.modalSaveBtnText}>{saveLabel}</Text></TouchableOpacity>
            : <View style={{width:60}} />
          }
        </View>
        <ScrollView style={{flex:1}} keyboardShouldPersistTaps="handled">
          <View style={{padding:20}}>{children}</View>
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );

  const Field = React.memo(({label, value, onChange, placeholder, numeric, multiline, half}) => {
    const [local, setLocal] = React.useState(value || '');
    React.useEffect(() => { setLocal(value || ''); }, [value]);
    return (
      <View style={[S.field, half && {flex:1,marginHorizontal:4}]}>
        <Text style={S.fieldLabel}>{label}</Text>
        <TextInput style={[S.fieldInput, multiline && S.fieldTextarea]}
          value={local}
          onChangeText={setLocal}
          onBlur={() => onChange(local)}
          onEndEditing={(e) => onChange(e.nativeEvent.text || local)}
          placeholder={placeholder}
          placeholderTextColor="#aaa"
          keyboardType={numeric ? 'numeric' : 'default'}
          multiline={multiline}
          numberOfLines={multiline ? 3 : 1} />
      </View>
    );
  });

  const renderModal = () => {
    if (!showModal) return null;
    switch (modalType) {

      case 'createInvoice': return (
        <ModalShell title="Create Invoice" onSave={handleCreateInvoice} saveLabel="Create">
          <Field label="Client Name *" value={newInvoice.clientName} onChange={v=>setNewInvoice(p=>({...p,clientName:v}))} placeholder="ABC Ltd" />
          <Field label="Client Email" value={newInvoice.clientEmail} onChange={v=>setNewInvoice(p=>({...p,clientEmail:v}))} placeholder="accounts@client.co.uk" />
          <Field label="Client Address" value={newInvoice.clientAddress} onChange={v=>setNewInvoice(p=>({...p,clientAddress:v}))} placeholder="Address" multiline />
          <Field label="Description *" value={newInvoice.description} onChange={v=>setNewInvoice(p=>({...p,description:v}))} placeholder="Services rendered" multiline />
          <View style={{flexDirection:'row'}}>
            <Field label="Amount (£) *" value={newInvoice.amount} onChange={v=>setNewInvoice(p=>({...p,amount:v}))} placeholder="0.00" numeric half />
            <Field label="VAT Rate (%)" value={newInvoice.vatRate} onChange={v=>setNewInvoice(p=>({...p,vatRate:v}))} placeholder="20" numeric half />
          </View>
          <View style={{flexDirection:'row'}}>
            <Field label="Due Date" value={newInvoice.dueDate} onChange={v=>setNewInvoice(p=>({...p,dueDate:v}))} placeholder="YYYY-MM-DD" half />
            <Field label="Payment Terms" value={newInvoice.paymentTerms} onChange={v=>setNewInvoice(p=>({...p,paymentTerms:v}))} placeholder="30 days" half />
          </View>
          <Field label="Notes" value={newInvoice.notes} onChange={v=>setNewInvoice(p=>({...p,notes:v}))} placeholder="Additional notes" multiline />
          {newInvoice.amount ? (
            <View style={S.vatBox}>
              <Text style={S.vatBoxTitle}>VAT Preview</Text>
              {[['Net',parseFloat(newInvoice.amount||0)],['VAT',parseFloat(newInvoice.amount||0)*parseFloat(newInvoice.vatRate||0)/100],['Total',parseFloat(newInvoice.amount||0)*(1+parseFloat(newInvoice.vatRate||0)/100)]].map(([l,v]) => (
                <View key={l} style={{flexDirection:'row',justifyContent:'space-between',paddingVertical:3}}>
                  <Text style={{color:'#555'}}>{l}:</Text><Text style={{fontWeight:'600'}}>£{v.toFixed(2)}</Text>
                </View>
              ))}
            </View>
          ) : null}
        </ModalShell>
      );

      case 'addExpense': return (
        <ModalShell title="Add Expense" onSave={handleCreateExpense}>
          <Field label="Description *" value={newExpense.description} onChange={v=>setNewExpense(p=>({...p,description:v}))} placeholder="What was this expense?" />
          <View style={{flexDirection:'row'}}>
            <Field label="Amount (£) *" value={newExpense.amount} onChange={v=>setNewExpense(p=>({...p,amount:v}))} placeholder="0.00" numeric half />
            <Field label="VAT Reclaim (£)" value={newExpense.vatReclaim} onChange={v=>setNewExpense(p=>({...p,vatReclaim:v}))} placeholder="0.00" numeric half />
          </View>
          <Text style={S.fieldLabel}>Category</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{marginBottom:16}}>
            {EXPENSE_CATEGORIES.map(cat => (
              <TouchableOpacity key={cat} style={[S.chip, newExpense.category===cat && S.chipActive, {marginRight:6}]} onPress={() => setNewExpense(p=>({...p,category:cat}))}>
                <Text style={[S.chipText, newExpense.category===cat && S.chipTextActive]}>{cat}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
          <View style={{flexDirection:'row'}}>
            <Field label="Date" value={newExpense.date} onChange={v=>setNewExpense(p=>({...p,date:v}))} placeholder="YYYY-MM-DD" half />
            <Field label="Supplier" value={newExpense.supplier} onChange={v=>setNewExpense(p=>({...p,supplier:v}))} placeholder="Supplier name" half />
          </View>
          <Field label="Business Purpose" value={newExpense.businessPurpose} onChange={v=>setNewExpense(p=>({...p,businessPurpose:v}))} placeholder="Why was this needed?" multiline />
        </ModalShell>
      );

      case 'createBudget': return (
        <ModalShell title="Create Budget" onSave={handleCreateBudget} saveLabel="Create">
          <Text style={S.fieldLabel}>Category *</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{marginBottom:16}}>
            {EXPENSE_CATEGORIES.map(cat => (
              <TouchableOpacity key={cat} style={[S.chip, newBudget.category===cat && S.chipActive, {marginRight:6}]} onPress={() => setNewBudget(p=>({...p,category:cat}))}>
                <Text style={[S.chipText, newBudget.category===cat && S.chipTextActive]}>{cat}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
          <View style={{flexDirection:'row'}}>
            <Field label="Allocated (£) *" value={newBudget.allocated} onChange={v=>setNewBudget(p=>({...p,allocated:v}))} placeholder="0.00" numeric half />
            <Field label="Period" value={newBudget.period} onChange={v=>setNewBudget(p=>({...p,period:v}))} placeholder="monthly" half />
          </View>
          <Field label="Notes" value={newBudget.notes} onChange={v=>setNewBudget(p=>({...p,notes:v}))} placeholder="Optional notes" multiline />
        </ModalShell>
      );

      case 'createTax': return (
        <ModalShell title="Add Tax Obligation" onSave={handleCreateTax}>
          <Text style={S.fieldLabel}>Tax Type *</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{marginBottom:16}}>
            {TAX_TYPES.map(type => (
              <TouchableOpacity key={type} style={[S.chip, newTax.type===type && S.chipActive, {marginRight:6}]} onPress={() => setNewTax(p=>({...p,type}))}>
                <Text style={[S.chipText, newTax.type===type && S.chipTextActive]}>{type}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
          <Field label="Description" value={newTax.description} onChange={v=>setNewTax(p=>({...p,description:v}))} placeholder="e.g. Q4 2023 VAT Return" />
          <View style={{flexDirection:'row'}}>
            <Field label="Amount (£)" value={newTax.amount} onChange={v=>setNewTax(p=>({...p,amount:v}))} placeholder="0.00" numeric half />
            <Field label="Due Date *" value={newTax.dueDate} onChange={v=>setNewTax(p=>({...p,dueDate:v}))} placeholder="YYYY-MM-DD" half />
          </View>
          <View style={{flexDirection:'row'}}>
            <Field label="Reference" value={newTax.reference} onChange={v=>setNewTax(p=>({...p,reference:v}))} placeholder="VAT-Q4-2023" half />
            <Field label="Penalty if late" value={newTax.penaltyIfLate} onChange={v=>setNewTax(p=>({...p,penaltyIfLate:v}))} placeholder="£0" half />
          </View>
        </ModalShell>
      );

      case 'addBank': return (
        <ModalShell title="Add Bank Account" onSave={handleCreateBankAccount}>
          <Field label="Account Name *" value={newBank.accountName} onChange={v=>setNewBank(p=>({...p,accountName:v}))} placeholder="Main Business Account" />
          <Field label="Bank Name *" value={newBank.bankName} onChange={v=>setNewBank(p=>({...p,bankName:v}))} placeholder="Barclays" />
          <View style={{flexDirection:'row'}}>
            <Field label="Account Number" value={newBank.accountNumber} onChange={v=>setNewBank(p=>({...p,accountNumber:v}))} placeholder="12345678" numeric half />
            <Field label="Sort Code" value={newBank.sortCode} onChange={v=>setNewBank(p=>({...p,sortCode:v}))} placeholder="20-00-00" half />
          </View>
          <View style={{flexDirection:'row'}}>
            <Field label="Currency" value={newBank.currency} onChange={v=>setNewBank(p=>({...p,currency:v}))} placeholder="GBP" half />
            <Field label="Balance (£)" value={newBank.balance} onChange={v=>setNewBank(p=>({...p,balance:v}))} placeholder="0.00" numeric half />
          </View>
          <Field label="IBAN" value={newBank.iban} onChange={v=>setNewBank(p=>({...p,iban:v}))} placeholder="GB00 BARC 0000 0000 0000 00" />
          <Field label="SWIFT/BIC" value={newBank.swift} onChange={v=>setNewBank(p=>({...p,swift:v}))} placeholder="BARCGB22" />
          <View style={{flexDirection:'row',alignItems:'center',marginTop:8}}>
            <Switch value={newBank.isDefault} onValueChange={v=>setNewBank(p=>({...p,isDefault:v}))} />
            <Text style={{marginLeft:8,color:'#374151'}}>Set as default account</Text>
          </View>
        </ModalShell>
      );

      case 'taxCalculator': return (
        <ModalShell title="UK Tax Calculator 2024/25">
          <Text style={S.fieldLabel}>Employment Status</Text>
          <View style={{flexDirection:'row',marginBottom:16}}>
            {[['selfEmployed','Self-Employed'],['employed','Employed'],['company','Ltd Company']].map(([k,l]) => (
              <TouchableOpacity key={k} style={[S.empBtn, taxCalc.employmentStatus===k && S.empBtnActive, {flex:1,marginHorizontal:3}]} onPress={() => setTaxCalc(p=>({...p,employmentStatus:k,results:null}))}>
                <Text style={[S.empBtnText, taxCalc.employmentStatus===k && S.empBtnTextActive]}>{l}</Text>
              </TouchableOpacity>
            ))}
          </View>
          <Field label={taxCalc.employmentStatus==='company' ? 'Company Profit (£)' : taxCalc.employmentStatus==='employed' ? 'Annual Salary (£)' : 'Annual Profit (£)'}
            value={taxCalc.income} onChange={v=>setTaxCalc(p=>({...p,income:v,results:null}))} placeholder="50000" numeric />
          {taxCalc.employmentStatus === 'selfEmployed' && (
            <Field label="Allowable Expenses (£)" value={taxCalc.expenses} onChange={v=>setTaxCalc(p=>({...p,expenses:v,results:null}))} placeholder="0.00" numeric />
          )}
          <TouchableOpacity style={[S.createBtn,{marginVertical:16}]} onPress={runTaxCalc}>
            <Ionicons name="calculator" size={20} color="#fff" /><Text style={S.createBtnText}>Calculate</Text>
          </TouchableOpacity>
          {taxCalc.results && (
            <View style={S.resultsBox}>
              <Text style={S.resultsTitle}>Tax Calculation Results</Text>
              {[
                ['Gross Income', taxCalc.results.grossIncome],
                taxCalc.results.allowableExpenses > 0 ? ['Allowable Expenses', -taxCalc.results.allowableExpenses] : null,
                ['Net Income', taxCalc.results.netIncome],
                ['— Income Tax', taxCalc.results.incomeTax],
                taxCalc.results.nationalInsurance > 0 ? ['— National Insurance', taxCalc.results.nationalInsurance] : null,
                taxCalc.results.corporationTax > 0 ? ['— Corporation Tax', taxCalc.results.corporationTax] : null,
                ['Total Tax', taxCalc.results.totalTax],
                ['Net After Tax', taxCalc.results.netAfterTax],
              ].filter(Boolean).map(([label, val]) => (
                <View key={label} style={{flexDirection:'row',justifyContent:'space-between',paddingVertical:6,borderBottomWidth:label==='Net After Tax'?0:1,borderBottomColor:'#EEE'}}>
                  <Text style={{color:label==='Net After Tax'||label==='Total Tax'?'#1F2937':'#555',fontWeight:label==='Net After Tax'||label==='Total Tax'?'bold':'normal'}}>{label}</Text>
                  <Text style={{color:label==='Net After Tax'?'#10B981':label==='Total Tax'?'#EF4444':'#1F2937',fontWeight:'600'}}>£{Math.abs(val||0).toLocaleString()}</Text>
                </View>
              ))}
              {taxCalc.results.breakdown && taxCalc.results.breakdown.length > 0 && (
                <View style={{marginTop:12,backgroundColor:'#F9FAFB',padding:12,borderRadius:8}}>
                  <Text style={{fontWeight:'600',marginBottom:6}}>Breakdown</Text>
                  {taxCalc.results.breakdown.map((b,i) => <Text key={i} style={{color:'#555',fontSize:13,marginBottom:3}}>• {b}</Text>)}
                </View>
              )}
            </View>
          )}
        </ModalShell>
      );

      case 'invoiceDetails': return selectedItem ? (
        <ModalShell title="Invoice">
          <View style={{backgroundColor:'#0e4e96',borderRadius:12,padding:16,marginBottom:16}}>
            <View style={{flexDirection:'row',alignItems:'center',gap:12}}>
              {companyLogo && typeof companyLogo === 'string' && companyLogo.startsWith('http') || logo.startsWith('data:') ? (
                <Image source={{uri:companyLogo}} style={{width:44,height:44,borderRadius:10}} resizeMode="contain" onError={()=>{}} />
              ) : (
                <View style={{width:44,height:44,borderRadius:10,backgroundColor:'rgba(255,255,255,0.15)',alignItems:'center',justifyContent:'center'}}>
                  <MaterialCommunityIcons name="office-building" size={22} color="#fff" />
                </View>
              )}
              <View style={{flex:1}}>
                <Text style={{fontSize:17,fontWeight:'900',color:'#fff'}}>{companyName || 'JobEng Ltd'}</Text>
                <Text style={{fontSize:9,color:'rgba(255,255,255,0.6)',marginTop:2}}>{companyProfile?.email || 'info@jobeng.ai'}{companyProfile?.phone ? ' | '+companyProfile.phone : ''}</Text>
              </View>
            </View>
            {companyProfile?.vat_number ? <Text style={{fontSize:8,color:'rgba(255,255,255,0.4)',marginTop:6}}>{'VAT: '+companyProfile.vat_number}{companyProfile?.registration_number ? ' | Reg: '+companyProfile.registration_number : ''}</Text> : null}
          </View>

          <View style={{flexDirection:'row',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
            <View>
              <Text style={{fontSize:20,fontWeight:'900',color:'#0e4e96'}}>INVOICE</Text>
              <Text style={{fontSize:13,fontWeight:'700',color:'#334155'}}>{selectedItem.invoiceNumber || ''}</Text>
            </View>
            <View style={{backgroundColor:selectedItem.status==='paid'?'#D1FAE5':selectedItem.status==='overdue'?'#FEE2E2':'#FEF3C7',paddingHorizontal:12,paddingVertical:5,borderRadius:8}}>
              <Text style={{fontSize:11,fontWeight:'700',color:selectedItem.status==='paid'?'#065F46':selectedItem.status==='overdue'?'#991B1B':'#92400E'}}>{(selectedItem.status||'draft').toUpperCase()}</Text>
            </View>
          </View>

          <View style={{backgroundColor:'#F8FAFC',borderRadius:10,padding:12,marginBottom:14,borderLeftWidth:3,borderLeftColor:'#0e4e96'}}>
            <Text style={{fontSize:9,fontWeight:'700',color:'#94A3B8',letterSpacing:1,marginBottom:4}}>BILL TO</Text>
            <Text style={{fontSize:15,fontWeight:'800',color:'#1E293B'}}>{selectedItem.clientName || 'Client'}</Text>
            {selectedItem.clientEmail ? <Text style={{fontSize:11,color:'#64748B',marginTop:2}}>{selectedItem.clientEmail}</Text> : null}
            {selectedItem.clientAddress ? <Text style={{fontSize:11,color:'#64748B',marginTop:1}}>{selectedItem.clientAddress}</Text> : null}
          </View>

          <View style={{borderWidth:1,borderColor:'#E2E8F0',borderRadius:10,marginBottom:14,overflow:'hidden'}}>
            {[{l:'Description',v:selectedItem.description},{l:'Issued',v:selectedItem.dateCreated},{l:'Due Date',v:selectedItem.dueDate},{l:'Terms',v:(companyProfile?.payment_terms||'30')+' days'},{l:'VAT Rate',v:(selectedItem.vatRate||'20')+'%'}].filter(x => x.v).map((x, idx) => (
              <View key={x.l} style={{flexDirection:'row',paddingVertical:9,paddingHorizontal:12,borderBottomWidth:idx<4?1:0,borderBottomColor:'#F1F5F9',backgroundColor:idx%2===0?'#fff':'#F8FAFC'}}>
                <Text style={{width:90,fontSize:11,color:'#64748B'}}>{x.l}</Text>
                <Text style={{flex:1,fontSize:11,fontWeight:'600',color:'#1E293B'}}>{x.v}</Text>
              </View>
            ))}
          </View>



          <View style={{backgroundColor:'#F0F4F8',borderRadius:10,padding:14,marginBottom:14}}>
            <View style={{flexDirection:'row',justifyContent:'space-between',marginBottom:6}}>
              <Text style={{fontSize:12,color:'#64748B'}}>Subtotal</Text>
              <Text style={{fontSize:12,fontWeight:'600',color:'#1E293B'}}>{'\u00A3'+(parseFloat(selectedItem.amount||0)).toLocaleString('en-GB',{minimumFractionDigits:2})}</Text>
            </View>
            <View style={{flexDirection:'row',justifyContent:'space-between',marginBottom:6}}>
              <Text style={{fontSize:12,color:'#64748B'}}>{'VAT ('+(selectedItem.vatRate||'20')+'%)'}</Text>
              <Text style={{fontSize:12,fontWeight:'600',color:'#1E293B'}}>{'\u00A3'+(parseFloat(selectedItem.vatAmount||0)).toLocaleString('en-GB',{minimumFractionDigits:2})}</Text>
            </View>
            <View style={{height:2,backgroundColor:'#0e4e96',marginVertical:6}} />
            <View style={{flexDirection:'row',justifyContent:'space-between'}}>
              <Text style={{fontSize:15,fontWeight:'900',color:'#0e4e96'}}>TOTAL DUE</Text>
              <Text style={{fontSize:17,fontWeight:'900',color:'#0e4e96'}}>{'\u00A3'+(parseFloat(selectedItem.totalAmount||0)).toLocaleString('en-GB',{minimumFractionDigits:2})}</Text>
            </View>
          </View>

          {companyProfile && companyProfile.bank_name ? (
            <View style={{backgroundColor:'#EFF6FF',borderRadius:10,padding:12,marginBottom:14,borderWidth:1,borderColor:'#BFDBFE'}}>
              <Text style={{fontSize:10,fontWeight:'700',color:'#0e4e96',letterSpacing:1,marginBottom:6}}>PAYMENT DETAILS</Text>
              {[{l:'Bank',v:companyProfile.bank_name},{l:'Account',v:companyProfile.bank_account},{l:'Sort Code',v:companyProfile.bank_sort_code},{l:'IBAN',v:companyProfile.bank_iban}].filter(x => x.v).map((x) => (
                <View key={x.l} style={{flexDirection:'row',justifyContent:'space-between',paddingVertical:2}}>
                  <Text style={{fontSize:10,color:'#3B82F6'}}>{x.l}</Text>
                  <Text style={{fontSize:10,fontWeight:'700',color:'#1E293B'}}>{x.v}</Text>
                </View>
              ))}
            </View>
          ) : null}

          <View style={{gap:8}}>
            {selectedItem.status !== 'paid' ? (
              <TouchableOpacity style={{flexDirection:'row',alignItems:'center',justifyContent:'center',backgroundColor:'#10B981',paddingVertical:13,borderRadius:12,gap:8}}
                onPress={() => {handleUpdateInvoiceStatus(selectedItem.id,'paid');setShowModal(false);}}>
                <Ionicons name="checkmark-circle" size={18} color="#fff" />
                <Text style={{fontSize:14,fontWeight:'700',color:'#fff'}}>Mark as Paid</Text>
              </TouchableOpacity>
            ) : null}
            {selectedItem.status === 'draft' ? (
              <TouchableOpacity style={{flexDirection:'row',alignItems:'center',justifyContent:'center',backgroundColor:'#8B5CF6',paddingVertical:13,borderRadius:12,gap:8}}
                onPress={() => {handleUpdateInvoiceStatus(selectedItem.id,'sent');setShowModal(false);}}>
                <Ionicons name="send" size={18} color="#fff" />
                <Text style={{fontSize:14,fontWeight:'700',color:'#fff'}}>Send to Client</Text>
              </TouchableOpacity>
            ) : null}
            <View style={{flexDirection:'row',gap:8}}>
              <TouchableOpacity style={{flex:1,flexDirection:'row',alignItems:'center',justifyContent:'center',backgroundColor:'#0e4e96',paddingVertical:11,borderRadius:10,gap:5}}
                onPress={() => generateInvoicePDF(selectedItem, 'share')}>
                <Ionicons name="share-outline" size={15} color="#fff" />
                <Text style={{fontSize:11,fontWeight:'700',color:'#fff'}}>Share</Text>
              </TouchableOpacity>
              <TouchableOpacity style={{flex:1,flexDirection:'row',alignItems:'center',justifyContent:'center',backgroundColor:'#EA580C',paddingVertical:11,borderRadius:10,gap:5}}
                onPress={() => generateInvoicePDF(selectedItem, 'email')}>
                <Ionicons name="mail-outline" size={15} color="#fff" />
                <Text style={{fontSize:11,fontWeight:'700',color:'#fff'}}>Email</Text>
              </TouchableOpacity>
              <TouchableOpacity style={{flex:1,flexDirection:'row',alignItems:'center',justifyContent:'center',backgroundColor:'#334155',paddingVertical:11,borderRadius:10,gap:5}}
                onPress={() => generateInvoicePDF(selectedItem, 'download')}>
                <Ionicons name="download-outline" size={15} color="#fff" />
                <Text style={{fontSize:11,fontWeight:'700',color:'#fff'}}>PDF</Text>
              </TouchableOpacity>
            </View>
          </View>

          <View style={{alignItems:'center',marginTop:16,paddingTop:10,borderTopWidth:1,borderTopColor:'#E2E8F0'}}>
            <Text style={{fontSize:9,color:'#94A3B8'}}>{companyName || 'JobEng Ltd'} | {companyProfile?.email || 'info@jobeng.ai'}</Text>
          </View>
        </ModalShell>
      ) : null;

      case 'generateReport': return (
        <ModalShell title="Generate Report" onSave={handleGenerateReport} saveLabel="Export PDF">
          {reportType ? (
            <View style={[S.card,{margin:0,marginBottom:16,borderLeftWidth:4,borderLeftColor:'#667eea'}]}>
              <Text style={S.cardTitle}>{reportType}</Text>
              <Text style={S.cardSub}>Selected report type</Text>
            </View>
          ) : (
            <Text style={{color:'#888',marginBottom:12}}>Select a report type from the Reports tab first.</Text>
          )}
          <Text style={S.fieldLabel}>Report Period</Text>
          <View style={{flexDirection:'row',marginBottom:16}}>
            {['monthly','quarterly','annual'].map(p => (
              <TouchableOpacity key={p} style={[S.empBtn,{flex:1,marginHorizontal:4},reportPeriod===p && S.empBtnActive]} onPress={() => setReportPeriod(p)}>
                <Text style={[S.empBtnText, reportPeriod===p && S.empBtnTextActive]}>{p.charAt(0).toUpperCase()+p.slice(1)}</Text>
              </TouchableOpacity>
            ))}
          </View>
          <View style={S.card}>
            <View style={{flexDirection:'row',alignItems:'center'}}>
              <Ionicons name="document-text" size={24} color="#EF4444" />
              <View style={{marginLeft:12}}>
                <Text style={{fontWeight:'600',color:'#1F2937'}}>PDF Format</Text>
                <Text style={{color:'#888',fontSize:13}}>Professional report ready for sharing</Text>
              </View>
            </View>
          </View>
        </ModalShell>
      );

      default: return null;
    }
  };

  // ─── Root Render ────────────────────────────────────────────────────────────
  // ═════════════ CASH FLOW TAB ═════════════
  const renderCashFlowMgmt = () => {
    const totalRev = invoices.reduce((s, i) => s + parseFloat(i.total_amount || i.total || i.amount || 0), 0);
    const totalExp = expenses.reduce((s, e) => s + parseFloat(e.amount || e.total || 0), 0);
    const outstanding = invoices.filter(i => (i.status||'').toLowerCase() !== 'paid').reduce((s, i) => s + parseFloat(i.total_amount || i.total || i.amount || 0), 0);
    const netCash = totalRev - totalExp;
    const monthlyBurn = totalExp / 12;

    return (
      <ScrollView style={{ flex:1 }} contentContainerStyle={{ padding:16, paddingBottom:40 }}>
        <View style={{ flexDirection:'row', gap:8, marginBottom:12 }}>
          {[{ l:'Inflows', v:'\u00A3'+(totalRev/1000).toFixed(1)+'K', c:'#10B981', i:'arrow-down-bold-circle' },
            { l:'Outflows', v:'\u00A3'+(totalExp/1000).toFixed(1)+'K', c:'#EF4444', i:'arrow-up-bold-circle' },
            { l:'Net Cash', v:'\u00A3'+(netCash/1000).toFixed(1)+'K', c:netCash>=0?'#10B981':'#EF4444', i:'bank-transfer' },
          ].map((k,i) => (
            <View key={i} style={{ flex:1, backgroundColor:'#fff', borderRadius:14, padding:14, alignItems:'center', borderWidth:1, borderColor:'#E2E8F0' }}>
              <MaterialCommunityIcons name={k.i} size={22} color={k.c} />
              <Text style={{ fontSize:16, fontWeight:'900', color:k.c, marginTop:4 }}>{k.v}</Text>
              <Text style={{ fontSize:9, color:'#94A3B8' }}>{k.l}</Text>
            </View>
          ))}
        </View>
        <View style={S.sectionCard}>
          <Text style={S.sectionCardTitle}>Cash Flow Statement</Text>
          {[{ l:'Revenue Received', v:totalRev - outstanding, c:'#10B981' },
            { l:'Outstanding Receivables', v:outstanding, c:'#F59E0B' },
            { l:'Operating Expenses', v:totalExp, c:'#EF4444' },
            { l:'Monthly Burn Rate', v:monthlyBurn, c:'#8B5CF6' },
          ].map((r,i) => (
            <View key={i} style={{ flexDirection:'row', justifyContent:'space-between', paddingVertical:10, borderBottomWidth:i<3?1:0, borderBottomColor:'#F1F5F9' }}>
              <Text style={{ fontSize:13, color:'#475569' }}>{r.l}</Text>
              <Text style={{ fontSize:14, fontWeight:'700', color:r.c }}>\u00A3{parseFloat(r.v||0).toLocaleString('en-GB',{minimumFractionDigits:2})}</Text>
            </View>
          ))}
          <View style={{ borderTopWidth:2, borderTopColor:'#0F172A', marginTop:8, paddingTop:10, flexDirection:'row', justifyContent:'space-between' }}>
            <Text style={{ fontSize:15, fontWeight:'900', color:'#0F172A' }}>Net Cash Position</Text>
            <Text style={{ fontSize:15, fontWeight:'900', color: netCash>=0?'#10B981':'#EF4444' }}>\u00A3{netCash.toLocaleString('en-GB',{minimumFractionDigits:2})}</Text>
          </View>
        </View>
        <View style={S.sectionCard}>
          <Text style={S.sectionCardTitle}>Cash Flow Ratios</Text>
          {[{ l:'Operating CF Ratio', v: totalRev > 0 ? ((netCash/totalRev)*100).toFixed(1)+'%' : '0%' },
            { l:'Cash Conversion', v: totalRev > 0 ? (((totalRev-outstanding)/totalRev)*100).toFixed(1)+'%' : '0%' },
            { l:'Expense Ratio', v: totalRev > 0 ? ((totalExp/totalRev)*100).toFixed(1)+'%' : '0%' },
            { l:'Runway', v: monthlyBurn > 0 ? (netCash/monthlyBurn).toFixed(1)+' months' : 'N/A' },
          ].map((r,i) => (
            <View key={i} style={{ flexDirection:'row', justifyContent:'space-between', paddingVertical:10, borderBottomWidth:i<3?1:0, borderBottomColor:'#F1F5F9' }}>
              <Text style={{ fontSize:13, color:'#334155', fontWeight:'600' }}>{r.l}</Text>
              <Text style={{ fontSize:14, fontWeight:'800', color:'#0e4e96' }}>{r.v}</Text>
            </View>
          ))}
        </View>
      </ScrollView>
    );
  };

  // ═════════════ FORECAST TAB ═════════════
  const renderForecastMgmt = () => {
    const totalRev = invoices.reduce((s, i) => s + parseFloat(i.total_amount || i.total || i.amount || 0), 0);
    const totalExp = expenses.reduce((s, e) => s + parseFloat(e.amount || e.total || 0), 0);
    const avgRev = totalRev / 12;
    const avgExp = totalExp / 12;

    return (
      <ScrollView style={{ flex:1 }} contentContainerStyle={{ padding:16, paddingBottom:40 }}>
        <View style={{ flexDirection:'row', gap:8, marginBottom:12 }}>
          {[{ l:'Rev (3mo)', v:'\u00A3'+((avgRev*3)/1000).toFixed(1)+'K', c:'#10B981' },
            { l:'Exp (3mo)', v:'\u00A3'+((avgExp*3)/1000).toFixed(1)+'K', c:'#EF4444' },
            { l:'Profit (3mo)', v:'\u00A3'+(((avgRev-avgExp)*3)/1000).toFixed(1)+'K', c: avgRev>avgExp?'#10B981':'#EF4444' },
          ].map((k,i) => (
            <View key={i} style={{ flex:1, backgroundColor:'#fff', borderRadius:14, padding:14, alignItems:'center', borderWidth:1, borderColor:'#E2E8F0' }}>
              <Text style={{ fontSize:16, fontWeight:'900', color:k.c }}>{k.v}</Text>
              <Text style={{ fontSize:9, color:'#94A3B8' }}>{k.l}</Text>
            </View>
          ))}
        </View>

        <View style={S.sectionCard}>
          <Text style={S.sectionCardTitle}>Revenue Forecast</Text>
          {[1,2,3,6,12].map((m,i) => (
            <View key={i} style={{ flexDirection:'row', justifyContent:'space-between', paddingVertical:10, borderBottomWidth:i<4?1:0, borderBottomColor:'#F1F5F9' }}>
              <Text style={{ fontSize:13, color:'#475569' }}>Next {m} month{m>1?'s':''}</Text>
              <Text style={{ fontSize:14, fontWeight:'700', color:'#10B981' }}>\u00A3{(avgRev*m).toLocaleString('en-GB',{minimumFractionDigits:0})}</Text>
            </View>
          ))}
        </View>

        <View style={S.sectionCard}>
          <Text style={S.sectionCardTitle}>Projected P&L</Text>
          <View style={{ flexDirection:'row', paddingVertical:8, borderBottomWidth:1, borderBottomColor:'#E2E8F0' }}>
            <Text style={{ fontSize:11, fontWeight:'700', color:'#64748B', width:50 }}>Period</Text>
            <Text style={{ fontSize:11, fontWeight:'700', color:'#10B981', flex:1, textAlign:'center' }}>Revenue</Text>
            <Text style={{ fontSize:11, fontWeight:'700', color:'#EF4444', flex:1, textAlign:'center' }}>Expenses</Text>
            <Text style={{ fontSize:11, fontWeight:'700', color:'#0e4e96', flex:1, textAlign:'right' }}>Profit</Text>
          </View>
          {[1,3,6,12].map((m,i) => {
            const pRev = avgRev * m;
            const pExp = avgExp * m;
            const pProfit = pRev - pExp;
            return (
              <View key={i} style={{ flexDirection:'row', paddingVertical:10, borderBottomWidth:i<3?1:0, borderBottomColor:'#F1F5F9' }}>
                <Text style={{ fontSize:12, color:'#475569', width:50 }}>{m}mo</Text>
                <Text style={{ fontSize:11, color:'#10B981', flex:1, textAlign:'center' }}>\u00A3{(pRev/1000).toFixed(1)}K</Text>
                <Text style={{ fontSize:11, color:'#EF4444', flex:1, textAlign:'center' }}>\u00A3{(pExp/1000).toFixed(1)}K</Text>
                <Text style={{ fontSize:12, fontWeight:'700', color:pProfit>=0?'#10B981':'#EF4444', flex:1, textAlign:'right' }}>\u00A3{(pProfit/1000).toFixed(1)}K</Text>
              </View>
            );
          })}
        </View>

        <View style={S.sectionCard}>
          <Text style={S.sectionCardTitle}>ROI Analysis</Text>
          {[{ l:'Total Investment', v:'\u00A3'+totalExp.toLocaleString('en-GB'), c:'#EF4444' },
            { l:'Total Return', v:'\u00A3'+totalRev.toLocaleString('en-GB'), c:'#10B981' },
            { l:'ROI', v: totalExp>0 ? (((totalRev-totalExp)/totalExp)*100).toFixed(1)+'%' : 'N/A', c: totalRev>totalExp?'#10B981':'#EF4444' },
            { l:'Payback Period', v: totalRev>0 ? ((totalExp/(totalRev/12))).toFixed(1)+' months' : 'N/A', c:'#0e4e96' },
          ].map((r,i) => (
            <View key={i} style={{ flexDirection:'row', justifyContent:'space-between', paddingVertical:10, borderBottomWidth:i<3?1:0, borderBottomColor:'#F1F5F9' }}>
              <Text style={{ fontSize:13, color:'#475569' }}>{r.l}</Text>
              <Text style={{ fontSize:14, fontWeight:'700', color:r.c }}>{r.v}</Text>
            </View>
          ))}
        </View>

        <View style={{ backgroundColor:'#FEF3C7', padding:12, borderRadius:10, marginTop:4 }}>
          <View style={{ flexDirection:'row', alignItems:'center', gap:6 }}>
            <MaterialCommunityIcons name="alert-circle-outline" size={16} color="#92400E" />
            <Text style={{ fontSize:12, fontWeight:'600', color:'#92400E' }}>Estimates Only</Text>
          </View>
          <Text style={{ fontSize:11, color:'#92400E', marginTop:4, lineHeight:16 }}>Forecasts are based on current run rates. Actual results may vary due to seasonal changes, market conditions, and business decisions.</Text>
        </View>
      </ScrollView>
    );
  };

  if (loading) return (
    <View style={[S.container,{justifyContent:'center',alignItems:'center'}]}>
      <ActivityIndicator size="large" color="#667eea" />
      <Text style={{color:'#667eea',marginTop:12}}>Loading financial data…</Text>
    </View>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':  return renderOverview();
      case 'invoices':  return renderInvoices();
      case 'expenses':  return renderExpenses();
      case 'budgets':   return renderBudgets();
      case 'taxes':     return renderTaxes();
      case 'banking':   return renderBanking();
      case 'analytics': return renderAnalytics();
      case 'reports':   return renderReports();
      case 'cashflow':  return renderCashFlowMgmt();
      case 'forecast':  return renderForecastMgmt();
      default:          return renderOverview();
    }
  };

  return (
    <View style={S.container}>
      <StatusBar barStyle="light-content" backgroundColor="#667eea" />
      {renderHeader()}
      {renderTabBar()}
      <View style={{flex:1}}>{renderContent()}</View>
      {renderModal()}
    </View>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const S = StyleSheet.create({
  container:       { flex:1, backgroundColor:'#F8FAFC' },
  header:          { paddingBottom:20 },
  headerContent:   { flexDirection:'row', alignItems:'center', paddingHorizontal:20, paddingTop:10 },
  headerBtn:       { width:40, height:40, borderRadius:20, backgroundColor:'rgba(255,255,255,0.2)', alignItems:'center', justifyContent:'center' },
  headerCenter:    { flex:1, alignItems:'center' },
  headerTitle:     { fontSize:20, fontWeight:'bold', color:'#fff' },
  headerSub:       { fontSize:13, color:'rgba(255,255,255,0.8)', marginTop:2 },
  tabBar:          { backgroundColor:'#fff', maxHeight:60, borderBottomWidth:1, borderBottomColor:'#E5E7EB' },
  tabBarContent:   { alignItems:'center', paddingHorizontal:8 },
  tabItem:         { alignItems:'center', paddingHorizontal:12, paddingVertical:10 },
  tabItemActive:   { borderBottomWidth:2, borderBottomColor:'#667eea' },
  tabText:         { fontSize:11, color:'#888', marginTop:3 },
  tabTextActive:   { color:'#667eea', fontWeight:'600' },
  // Cards
  card:            { backgroundColor:'#fff', marginHorizontal:16, marginBottom:12, padding:16, borderRadius:12, elevation:2, shadowColor:'#000', shadowOffset:{width:0,height:1}, shadowOpacity:0.08, shadowRadius:4 },
  cardHeader:      { flexDirection:'row', alignItems:'flex-start', marginBottom:8 },
  cardTitle:       { fontSize:16, fontWeight:'bold', color:'#1F2937' },
  cardSub:         { fontSize:13, color:'#6B7280', marginTop:2 },
  cardDetail:      { fontSize:14, color:'#374151', marginTop:4 },
  cardMeta:        { fontSize:12, color:'#9CA3AF', marginTop:4 },
  bigAmount:       { fontSize:20, fontWeight:'bold', color:'#1F2937' },
  // Metrics
  metricsGrid:     { flexDirection:'row', flexWrap:'wrap', padding:8 },
  metricCard:      { width:(width-48)/2, backgroundColor:'#fff', margin:4, padding:16, borderRadius:12, alignItems:'center', elevation:2, shadowColor:'#000', shadowOffset:{width:0,height:1}, shadowOpacity:0.08, shadowRadius:4 },
  metricVal:       { fontSize:18, fontWeight:'bold', color:'#1F2937', marginTop:6 },
  metricLabel:     { fontSize:11, color:'#6B7280', marginTop:3, textAlign:'center' },
  // Quick actions
  qaGrid:          { flexDirection:'row', flexWrap:'wrap', paddingHorizontal:8, marginBottom:8 },
  qaCard:          { width:(width-48)/2, backgroundColor:'#fff', margin:4, padding:16, borderRadius:12, alignItems:'center', elevation:2, shadowColor:'#000', shadowOffset:{width:0,height:1}, shadowOpacity:0.08, shadowRadius:4 },
  qaLabel:         { fontSize:13, color:'#374151', marginTop:8, fontWeight:'600', textAlign:'center' },
  sectionTitle:    { fontSize:17, fontWeight:'bold', color:'#1F2937', paddingHorizontal:16, paddingTop:16, paddingBottom:8 },
  // Row
  rowCard:         { backgroundColor:'#fff', marginHorizontal:16, marginBottom:8, padding:14, borderRadius:10, flexDirection:'row', alignItems:'center', justifyContent:'space-between', elevation:1, shadowColor:'#000', shadowOffset:{width:0,height:1}, shadowOpacity:0.06, shadowRadius:3 },
  rowTitle:        { fontSize:15, fontWeight:'600', color:'#1F2937' },
  rowSub:          { fontSize:12, color:'#9CA3AF', marginTop:2 },
  rowAmount:       { fontSize:16, fontWeight:'bold', color:'#1F2937' },
  // Badge
  badge:           { paddingHorizontal:7, paddingVertical:3, borderRadius:10 },
  badgeText:       { fontSize:10, fontWeight:'bold', color:'#fff' },
  badge_draft:     { backgroundColor:'#6B7280' },
  badge_sent:      { backgroundColor:'#3B82F6' },
  badge_paid:      { backgroundColor:'#10B981' },
  badge_overdue:   { backgroundColor:'#EF4444' },
  badge_pending:   { backgroundColor:'#F59E0B' },
  badge_completed: { backgroundColor:'#10B981' },
  badge_undefined: { backgroundColor:'#9CA3AF' },
  // Filters
  chipRow:         { paddingHorizontal:16, paddingVertical:8, maxHeight:50 },
  chip:            { paddingHorizontal:14, paddingVertical:6, borderRadius:20, borderWidth:1, borderColor:'#D1D5DB', marginRight:8, backgroundColor:'#fff' },
  chipActive:      { backgroundColor:'#667eea', borderColor:'#667eea' },
  chipText:        { fontSize:13, color:'#6B7280' },
  chipTextActive:  { color:'#fff', fontWeight:'600' },
  filterRow:       { paddingHorizontal:16, paddingTop:12 },
  searchBox:       { flexDirection:'row', alignItems:'center', backgroundColor:'#fff', paddingHorizontal:12, paddingVertical:10, borderRadius:10, elevation:1, shadowColor:'#000', shadowOffset:{width:0,height:1}, shadowOpacity:0.06, shadowRadius:3 },
  searchInput:     { flex:1, fontSize:15, color:'#1F2937', marginLeft:8 },
  // Create button
  createBtn:       { flexDirection:'row', alignItems:'center', justifyContent:'center', backgroundColor:'#667eea', marginHorizontal:16, marginVertical:8, paddingVertical:13, borderRadius:10, elevation:3, shadowColor:'#667eea', shadowOffset:{width:0,height:2}, shadowOpacity:0.3, shadowRadius:6 },
  createBtnText:   { color:'#fff', fontSize:15, fontWeight:'bold', marginLeft:6 },
  // Action row
  actionRow:       { flexDirection:'row', justifyContent:'flex-end', paddingTop:12, marginTop:4, borderTopWidth:1, borderTopColor:'#F3F4F6', gap:8 },
  actionBtn:       { flexDirection:'row', alignItems:'center', paddingHorizontal:10, paddingVertical:6, borderRadius:8, backgroundColor:'#F9FAFB' },
  actionBtnText:   { fontSize:12, color:'#374151', marginLeft:4, fontWeight:'600' },
  // Empty
  emptyText:       { textAlign:'center', color:'#9CA3AF', marginTop:40, fontSize:15 },
  // Summary
  summaryRow:      { flexDirection:'row', marginHorizontal:16, marginBottom:8, gap:8 },
  summaryCard:     { flex:1, backgroundColor:'#fff', padding:14, borderRadius:10, alignItems:'center', elevation:1 },
  summaryLabel:    { fontSize:12, color:'#6B7280' },
  summaryVal:      { fontSize:18, fontWeight:'bold', color:'#1F2937', marginTop:4 },
  // Budget
  budgetAmounts:   { flexDirection:'row', justifyContent:'space-between', marginVertical:8 },
  progressBg:      { height:8, backgroundColor:'#E5E7EB', borderRadius:4, marginVertical:6 },
  progressFill:    { height:8, borderRadius:4 },
  pctText:         { fontSize:13, fontWeight:'600', textAlign:'right' },
  // Tax
  taxCalcCard:     { backgroundColor:'#fff', margin:16, padding:16, borderRadius:12, elevation:2 },
  taxRatesCard:    { backgroundColor:'#fff', marginHorizontal:16, marginBottom:8, padding:16, borderRadius:12, elevation:2 },
  taxRateRow:      { flexDirection:'row', justifyContent:'space-between', paddingVertical:5, borderBottomWidth:1, borderBottomColor:'#F3F4F6' },
  taxRateLabel:    { fontSize:13, color:'#374151' },
  taxRateVal:      { fontSize:13, fontWeight:'600', color:'#667eea' },
  overdueCard:     { borderLeftWidth:4, borderLeftColor:'#EF4444' },
  // Reports
  reportCard:      { width:(width-56)/2, backgroundColor:'#fff', borderRadius:14, padding:16, marginBottom:16, elevation:2, shadowColor:'#000', shadowOffset:{width:0,height:1}, shadowOpacity:0.08, shadowRadius:4, borderLeftWidth:4 },
  reportTitle:     { fontSize:14, fontWeight:'bold', color:'#1E293B', marginTop:8, marginBottom:4 },
  reportDesc:      { fontSize:12, color:'#64748B', lineHeight:16 },
  // Modal
  modalShell:      { flex:1, backgroundColor:'#F8FAFC' },
  modalHeader:     { flexDirection:'row', alignItems:'center', justifyContent:'space-between', paddingHorizontal:20, paddingVertical:14, backgroundColor:'#fff', borderBottomWidth:1, borderBottomColor:'#E5E7EB' },
  modalClose:      { width:40, height:40, alignItems:'center', justifyContent:'center' },
  modalTitle:      { fontSize:18, fontWeight:'bold', color:'#1F2937' },
  modalSaveBtn:    { paddingHorizontal:16, paddingVertical:8, backgroundColor:'#667eea', borderRadius:8 },
  modalSaveBtnText:{ color:'#fff', fontSize:15, fontWeight:'bold' },
  // Form
  field:           { marginBottom:16 },
  fieldLabel:      { fontSize:14, fontWeight:'600', color:'#374151', marginBottom:6 },
  fieldInput:      { backgroundColor:'#fff', borderWidth:1, borderColor:'#D1D5DB', borderRadius:8, paddingHorizontal:14, paddingVertical:11, fontSize:15, color:'#1F2937' },
  fieldTextarea:   { height:80, textAlignVertical:'top' },
  vatBox:          { backgroundColor:'#F0F4FF', padding:14, borderRadius:10, marginTop:8 },
  vatBoxTitle:     { fontSize:15, fontWeight:'bold', color:'#1F2937', marginBottom:8 },
  // Calculator
  empBtn:          { paddingVertical:10, borderWidth:1.5, borderColor:'#D1D5DB', borderRadius:8, alignItems:'center' },
  empBtnActive:    { backgroundColor:'#667eea', borderColor:'#667eea' },
  empBtnText:      { fontSize:13, color:'#6B7280', fontWeight:'600' },
  empBtnTextActive:{ color:'#fff' },
  resultsBox:      { backgroundColor:'#fff', padding:16, borderRadius:12, elevation:2 },
  resultsTitle:    { fontSize:18, fontWeight:'bold', color:'#1F2937', marginBottom:12, textAlign:'center' },
});

export default FinancialManagementScreen;
